import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Trash, 
  Plus, 
  Warning,
  Info,
  ArrowLeft,
  MagicWand,
  Chat,
  Shield,
  Envelope,
  ChatCircle,
  PhoneTransfer,
  Code,
  WebhooksLogo,
  CloudSlash,
  Users,
  Buildings as Building,
  Calendar,
  Clock,
  ArrowsOutSimple,
  ArrowsOutSimple as Maximize2,
  Wrench,
  Notepad,
  Question,
  Question as HelpCircle,
  Gear,
  CaretDown,
  CaretUp,
  Hash,
  Envelope as Mail,
  Play,
  CheckCircle,
  XCircle,
  Lightning,
  Eye,
  EyeSlash,
  CircleNotch,
  PhoneTransfer as PhoneForwarded
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/text-area';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import NewCategoryModal from '../../brain/NewCategoryModal';
import SimpleActionToolModal from './SimpleActionToolModal';
import ApiActionTab from './ApiActionTab';
import EmailAddressInput from '../../components/EmailAddressInput';
import ApiHeadersInput from '../../components/ApiHeadersInput';
import ApiQueryParamsInput from '../../components/ApiQueryParamsInput';
import PhoneNumberInput from '../../components/PhoneNumberInput';
import MentionInput from '../ui/MentionInput';
import JsonMentionTextarea from '../ui/JsonMentionTextarea';
import { createPortal } from 'react-dom';
import { API_BASE_URL } from '../../config/api';
import BrainSettingConditions from '../brain/BrainSettingConditions'; // Import the new component
import { getUserAccountId } from '../../utils/accountUtils'; // Import account util

import { useLocations } from '../../hooks/use-locations';
import { runAutomaticConflictDetection } from '../../utils/conflictDetection';
import { refreshBrainSettings } from '../../hooks/usePageContext';

// Phone number formatting helpers - simple and elegant
const formatTransferNumberForDisplay = (e164Number) => {
  if (!e164Number) return '';
  
  // Remove +1 and any non-digits
  const cleaned = e164Number.replace(/^\+?1?/, '').replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX if we have 10 digits
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  return e164Number; // Return original if can't format
};

const convertToE164 = (formattedNumber) => {
  if (!formattedNumber) return '';
  
  // Remove all non-digits
  const cleaned = formattedNumber.replace(/\D/g, '');
  
  // Add +1 prefix for US numbers if we have 10 digits
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  
  // If already has country code, add + if missing
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  
  return formattedNumber; // Return original if unsure
};

const formatPhoneNumberAsUserTypes = (value) => {
  // Remove all non-digits
  const cleaned = value.replace(/\D/g, '');
  
  // Limit to 10 digits for US numbers
  const limited = cleaned.slice(0, 10);
  
  // Format progressively as user types
  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  } else {
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  }
};

// Skeleton component for loading state (Call Action Settings Modal)
const CallActionSettingsSkeleton = () => {
  return (
    <div className="space-y-4 sm:space-y-6 py-3 sm:py-4 animate-pulse">
      {/* Details Tab Skeleton */}
      {/* Name */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
      {/* Description */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        <div className="h-20 bg-gray-200 rounded w-full"></div>
      </div>
      {/* Tool Type Skeleton */}
      <div className="space-y-2 pt-3 border-t border-gray-100">
        <div className="h-4 bg-gray-200 rounded w-1/5"></div>
        <div className="h-12 bg-gray-200 rounded w-full"></div>
      </div>
      {/* Category Skeleton */}
      <div className="space-y-2 pt-3 border-t border-gray-100">
        <div className="h-4 bg-gray-200 rounded w-1/5"></div>
        <div className="h-10 bg-gray-200 rounded w-full"></div>
      </div>
      {/* Location Variants Toggle Skeleton */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="space-y-1 w-3/4">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        </div>
        <div className="h-6 w-12 bg-gray-200 rounded-full"></div>
      </div>
      {/* Active Status Toggle Skeleton */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="space-y-1 w-3/4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        </div>
        <div className="h-6 w-12 bg-gray-200 rounded-full"></div>
      </div>
    </div>
  );
};

// Animation variants - optimize for mobile performance
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: { 
    opacity: 0,
    transition: { delay: 0.1, duration: 0.2 }
  }
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { 
      duration: 0.3, 
      ease: [0.16, 1, 0.3, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: 20, 
    scale: 0.98,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

// Removed tabContentVariants - using instant tab switching for better performance

// Extracted ExpandedActionModal to avoid remount on each parent re-render
const ExpandedActionModal = React.memo(function ExpandedActionModal({
  expandedActionType,
  setExpandedActionType,
  formData,
  setFormData,
  validationErrors,
  setValidationErrors,
  setHasUnsavedChanges
}) {
  if (!expandedActionType) return null;
  
  return createPortal(
    <motion.div 
      className="fixed inset-0 bg-white z-[10002] flex flex-col overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white z-20 flex-shrink-0">
        <div className="flex items-center">
          <button
            onClick={() => setExpandedActionType(null)}
            className="mr-3 rounded-full h-8 w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="font-medium text-lg text-gray-900">
            {expandedActionType === 'sms' && 'Text Message'}
            {expandedActionType === 'email' && 'Email Message'}
            {expandedActionType === 'transfer' && 'Call Transfer'}
            {expandedActionType === 'api' && 'API Request'}
            {expandedActionType === 'dtmf' && 'DTMF Input'}
            {expandedActionType === 'note' && 'Take Note'}
          </h2>
        </div>
        <button
          onClick={() => setExpandedActionType(null)} 
          className="text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
        {/* SMS Content */}
        {expandedActionType === 'sms' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
              <div className="flex items-center text-sm text-blue-800">
                <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>You're editing the SMS message that will be sent when this tool is triggered.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <ChatCircle className="h-4 w-4 mr-2 text-blue-500" />
                  Text Message <span className="text-red-500 ml-0.5">*</span>
                </h3>
                
                <Textarea
                  id="expanded_sms_message"
                  value={formData.sms_message}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      sms_message: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                    
                    // Clear validation error if needed
                    if (validationErrors.sms_message) {
                      setValidationErrors(prev => {
                        const newErrors = {...prev};
                        delete newErrors.sms_message;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Enter the message to send when this tool is triggered"
                  className={cn(
                    "min-h-[200px] text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                    validationErrors.sms_message && "border-red-300 focus:border-red-500 focus:ring-red-500"
                  )}
                />
                
                <div className="flex justify-between items-center mt-2">
                  <div className={cn(
                    "text-xs", 
                    formData.sms_message.length > 160 ? "text-red-500" : "text-gray-500"
                  )}>
                    {formData.sms_message.length}/160 characters
                  </div>
                  
                  {validationErrors.sms_message && (
                    <p className="text-xs text-red-500">{validationErrors.sms_message}</p>
                  )}
                </div>
              </div>
              

              
              {/* Recipient Option in Expanded View */}
              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Send to specific numbers</h3>
                    <p className="text-xs text-gray-500">
                      {formData.sms_use_contact_phone 
                        ? "Uses pre-defined phone numbers" 
                        : "Uses caller's phone number automatically"}
                    </p>
                  </div>
                  <Switch
                    id="expanded_sms_use_contact_phone"
                    checked={formData.sms_use_contact_phone}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({ ...prev, sms_use_contact_phone: checked }));
                      setHasUnsavedChanges(true);
                    }}
                    className="data-[state=checked]:bg-blue-500"
                  />
                </div>
              </div>
              
              {/* Only show recipients field if use_contact_phone is true */}
              {formData.sms_use_contact_phone && (
                <div className="pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-blue-500" />
                    Recipients <span className="text-red-500 ml-0.5">*</span>
                  </h3>
                  
                  <PhoneNumberInput 
                    phoneNumbers={formData.sms_numbers} 
                    onChange={(numbers) => {
                      setFormData(prev => ({
                        ...prev,
                        sms_numbers: numbers
                      }));
                      setHasUnsavedChanges(true);
                      
                      // Clear validation error
                      if (validationErrors.sms_numbers) {
                        setValidationErrors(prev => {
                          const newErrors = {...prev};
                          delete newErrors.sms_numbers;
                          return newErrors;
                        });
                      }
                    }}
                    error={validationErrors.sms_numbers}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Email Content */}
        {expandedActionType === 'email' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6">
              <div className="flex items-center text-sm text-indigo-800">
                <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>You're editing the email that will be sent when this tool is triggered.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Envelope className="h-4 w-4 mr-2 text-indigo-500" />
                  Email Subject <span className="text-red-500 ml-0.5">*</span>
                </h3>
                
                <Input
                  id="expanded_email_subject"
                  value={formData.email_subject}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      email_subject: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                    
                    // Clear validation error if needed
                    if (validationErrors.email_subject) {
                      setValidationErrors(prev => {
                        const newErrors = {...prev};
                        delete newErrors.email_subject;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Enter email subject line"
                  className="h-10 text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
                
                {validationErrors.email_subject && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.email_subject}</p>
                )}
              </div>
              
              <div className="pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <ChatCircle className="h-4 w-4 mr-2 text-indigo-500" />
                  Email Message <span className="text-red-500 ml-0.5">*</span>
                </h3>
                
                <Textarea
                  id="expanded_email_message"
                  value={formData.email_message}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      email_message: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                    
                    // Clear validation error if needed
                    if (validationErrors.email_message) {
                      setValidationErrors(prev => {
                        const newErrors = {...prev};
                        delete newErrors.email_message;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Enter the email message to send when this tool is triggered"
                  className="min-h-[200px] text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
                
                {validationErrors.email_message && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.email_message}</p>
                )}
              </div>
              
              {/* Email Recipient Option */}
              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-1">Send to specific addresses</h3>
                    <p className="text-xs text-gray-500">
                      {formData.email_use_contact_email 
                        ? "Uses pre-defined email addresses" 
                        : "Asks caller for their email address"}
                    </p>
                  </div>
                  <Switch
                    id="expanded_email_use_contact_email"
                    checked={formData.email_use_contact_email}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({ ...prev, email_use_contact_email: checked }));
                      setHasUnsavedChanges(true);
                    }}
                    className="data-[state=checked]:bg-indigo-500"
                  />
                </div>
              </div>
              
              {/* Only show recipients field if use_contact_email is true */}
              {formData.email_use_contact_email && (
                <div className="pt-6 border-t border-gray-100">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2 text-indigo-500" />
                    Recipients <span className="text-red-500 ml-0.5">*</span>
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    Add email addresses that will receive this email
                  </p>
                  
                  <EmailAddressInput 
                    addresses={formData.email_addresses || []} // Ensure array
                    onChange={(addresses) => { // Updated onChange
                      if (formData.showLocationVariants) {
                        handleVariantChangeDirectly('email_addresses', addresses);
                      } else {
                        const sanitizedAddresses = typeof addresses === 'object' ? sanitizeFormData({ temp: addresses }).temp : addresses;
                        setFormData(prev => ({ ...prev, email_addresses: sanitizedAddresses }));
                        setHasUnsavedChanges(true);
                      }
                       // Clear validation (common logic)
                      if (validationErrors.email_addresses) {
                        setValidationErrors(prev => {
                          const newErrors = {...prev};
                          delete newErrors.email_addresses;
                          return newErrors;
                        });
                      }
                    }}
                    error={validationErrors.email_addresses}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* API Content */}
        {expandedActionType === 'api' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6">
              <div className="flex items-center text-sm text-purple-800">
                <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>You're configuring an API request that will be sent when this tool is triggered.</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Code className="h-4 w-4 mr-2 text-purple-500" />
                  HTTP Method
                </h3>
                
                <Select
                  value={formData.api_method}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, api_method: value }));
                    setHasUnsavedChanges(true);
                  }}
                >
                  <SelectTrigger className="w-full h-10 text-base border-gray-200 bg-gray-50 focus:bg-white">
                    <SelectValue placeholder="Select HTTP method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                    <SelectItem value="PATCH">PATCH</SelectItem>
                    <SelectItem value="DELETE">DELETE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <CloudSlash className="h-4 w-4 mr-2 text-purple-500" />
                  Request Headers
                </h3>
                <p className="text-xs text-gray-500 mb-2">
                  Add custom headers for your API request (authentication, content-type, etc.)
                </p>
                
                <ApiHeadersInput 
                  headers={formData.api_headers} 
                  onChange={(headers) => {
                        setFormData(prev => ({
                          ...prev,
                      api_headers: headers
                        }));
                        setHasUnsavedChanges(true);
                  }}
                    />
                  </div>
              
              {/* Only show request body for methods that support it */}
              {formData.api_method && ['POST', 'PUT', 'PATCH'].includes(formData.api_method) && (
                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <ChatCircle className="h-4 w-4 mr-2 text-purple-500" />
                    Request Body
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    Provide the JSON data to send with your request
                  </p>
                  
                  <Textarea
                    id="expanded_api_body"
                    value={formData.api_body}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                        api_body: e.target.value
                        }));
                        setHasUnsavedChanges(true);
                    }}
                    placeholder='{"key": "value", "data": "example"}'
                    className="min-h-[150px] text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors font-mono"
                    />
                  </div>
              )}
                </div>
          </div>
        )}
        
        {/* Transfer Content */}
        {expandedActionType === 'transfer' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 mb-6">
              <div className="flex items-center text-sm text-emerald-800">
                <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>You're configuring a call transfer that will forward the caller to another number when this tool is triggered.</p>
              </div>
              </div>
              
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <PhoneTransfer className="h-4 w-4 mr-2 text-emerald-500" />
                  Transfer Number <span className="text-red-500 ml-0.5">*</span>
                </h3>
                <p className="text-xs text-gray-500 mb-2">
                  Enter the phone number to transfer the call to
                </p>
                
                <Input
                  id="expanded_transfer_number"
                  value={formatTransferNumberForDisplay(formData.transfer_number)}
                  onChange={(e) => {
                    const formatted = formatPhoneNumberAsUserTypes(e.target.value);
                    const e164 = convertToE164(formatted);
                        
                        setFormData(prev => ({
                          ...prev,
                      transfer_number: e164
                        }));
                        setHasUnsavedChanges(true);
                        
                    // Clear validation error if needed
                    if (validationErrors.transfer_number) {
                          setValidationErrors(prev => {
                            const newErrors = {...prev};
                        delete newErrors.transfer_number;
                            return newErrors;
                          });
                        }
                      }}
                  placeholder="(555) 123-4567"
                  className="h-10 text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
                
                {validationErrors.transfer_number && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.transfer_number}</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* DTMF Content */}
        {expandedActionType === 'dtmf' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-6">
              <div className="flex items-center text-sm text-amber-800">
                <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>You're configuring DTMF input collection. This will prompt callers to enter digits using their keypad.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Hash className="h-4 w-4 mr-2 text-amber-500" />
                  DTMF Prompt <span className="text-red-500 ml-0.5">*</span>
                </h3>
                <p className="text-xs text-gray-500 mb-2">
                  What should the caller hear before entering digits?
                </p>
                
                <Textarea
                  id="expanded_dtmf_message"
                  value={formData.dtmf_message}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      dtmf_message: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                    
                    // Clear validation error if needed
                    if (validationErrors.dtmf_message) {
                      setValidationErrors(prev => {
                        const newErrors = {...prev};
                        delete newErrors.dtmf_message;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Please enter your account number followed by the pound key"
                  className="min-h-[100px] text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
                
                {validationErrors.dtmf_message && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.dtmf_message}</p>
                )}
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Maximum Digits</h3>
                <p className="text-xs text-gray-500 mb-2">
                  How many digits should we collect? (1-20)
                </p>
                
                <Input
                  id="expanded_dtmf_max_digits"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.dtmf_max_digits}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      dtmf_max_digits: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="10"
                  className="h-10 text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors w-32"
                />
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Timeout (seconds)</h3>
                <p className="text-xs text-gray-500 mb-2">
                  How long to wait for input before timing out? (1-30)
                </p>
                
                <Input
                  id="expanded_dtmf_timeout"
                  type="number"
                  min="1"
                  max="30"
                  value={formData.dtmf_timeout}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      dtmf_timeout: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="10"
                  className="h-10 text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors w-32"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Note Content */}
        {expandedActionType === 'note' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 mb-6">
              <div className="flex items-center text-sm text-slate-800">
                <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                <p>You're configuring note taking that will record information during the call when this tool is triggered.</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Notepad className="h-4 w-4 mr-2 text-slate-500" />
                  Note Title <span className="text-red-500 ml-0.5">*</span>
                </h3>
                <p className="text-xs text-gray-500 mb-2">
                  What should this note be called?
                </p>
                
                <Input
                  id="expanded_note_title"
                  value={formData.note_title}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      note_title: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                    
                    // Clear validation error if needed
                    if (validationErrors.note_title) {
                      setValidationErrors(prev => {
                        const newErrors = {...prev};
                        delete newErrors.note_title;
                        return newErrors;
                      });
                    }
                  }}
                  placeholder="Customer Feedback"
                  className="h-10 text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
                
                {validationErrors.note_title && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.note_title}</p>
                )}
            </div>
            
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <ChatCircle className="h-4 w-4 mr-2 text-slate-500" />
                  Note Content
                </h3>
                <p className="text-xs text-gray-500 mb-2">
                  Optional: Provide default note content or instructions
                </p>
                
                <Textarea
                  id="expanded_note_content"
                  value={formData.note_content}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      note_content: e.target.value
                    }));
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="Enter details about the customer interaction..."
                  className="min-h-[100px] text-base border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>,
    document.body
  );
});

const CallActionSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  action,
  locations = [],
  isNew = false,
  isInline = false, // Add isInline prop
  isPreLoaded = false, // Add isPreLoaded prop
  hideActionTab = false, // New prop to hide action tab for settings-level editing
  preSelectedCategoryId = null,
  onUpdateSetting, // Add new prop
  onCreateCategory, // Add new prop for category creation
  newlyCreatedCategory, // Add new prop for auto-selecting newly created category
  onClearNewlyCreatedCategory, // Add new prop for clearing newly created category
  onOpenActivationConditions,
  setting
}) => {
  // Get the business type and supabase client
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  
  // Fetch locations for variants
  const { locations: fetchedLocations, loading: locationsLoading } = useLocations(businessType);
  
  // State management
  const [activeTab, setActiveTab] = useState("details");
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    triggers: [],
    is_active: true,
    type: '', // Empty type initially for new actions
    category_id: preSelectedCategoryId || '',
    showLocationVariants: false, // Default to false
    all_locations: false, // Initialize all_locations
    selected_locations: [], // Initialize selected_locations
    
    // Questions fields
    questions: [], // Array of questions for simple actions
    
    // SMS fields
    sms_message: '',
    sms_numbers: [], // Array of phone numbers
    sms_use_contact_phone: false, // Default to false - will ask for phone number during the call
    
    // Email fields
    email_subject: '',
    email_message: '',
    email_addresses: [], // Array of email addresses
    email_use_contact_email: false, // Default to false - will ask for email during the call
    
    // Transfer Call fields
    transfer_number: '',
    transfer_name: '',
    transfer_days: [0,1,2,3,4,5,6], // Default to all days
    transfer_start_time: '09:00',
    transfer_end_time: '17:00',
    // play_message: false, // Removed
    // transfer_message: '', // Removed
    
    // API fields
    api_endpoint: '',
    api_method: 'POST',
    api_body: '',
    api_headers: {},
    api_query_params: {},
    api_response_template: '',
    auth_type: 'none',
    auth_config: {},

    // DTMF fields
    dtmf_number_to_press: '',

    // Note fields
    note_fields_config: [] // Will store the question-like field definitions for the note
  });
  
  // Add states for categories and new category modal
  const [categories, setCategories] = useState([]);

  
  const [currentTrigger, setCurrentTrigger] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [modalIsLoading, setModalIsLoading] = useState(false); // <-- Added loading state
  const initializedRef = useRef(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  // Add state for SimpleActionToolModal
  const [isToolTypeModalOpen, setIsToolTypeModalOpen] = useState(false);
  
  const [requirements, setRequirements] = useState([]); // Add state for requirements
  const [accountId, setAccountId] = useState(null); // Add state for accountId

  const [expandedActionType, setExpandedActionType] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentQuestionType, setCurrentQuestionType] = useState('string');

  // Add new state variables for location groupings and assignments
  const [locationGroupings, setLocationGroupings] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState({});
  const [hasLocationChanges, setHasLocationChanges] = useState(false);

  // Add state for active location grouping and form data by grouping
  const [activeLocationGrouping, setActiveLocationGrouping] = useState(null);
  const [formDataByGrouping, setFormDataByGrouping] = useState({});

  // --- State for Note Fields (similar to QuestionsTab in OrderPathwayBuilder) ---
  const [noteFields, setNoteFields] = useState([]); // Stores the fields for 'note' type
  // --- End State for Note Fields ---

  // Integration-based auth states
  const [useIntegrationAuth, setUseIntegrationAuth] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [availableProviders, setAvailableProviders] = useState([]);
  const [userIntegrationsMap, setUserIntegrationsMap] = useState({});

  // API Testing state
  const [isTestingAPI, setIsTestingAPI] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testInputs, setTestInputs] = useState({});
  const [showTestSection, setShowTestSection] = useState(false);

  // Response template toggle state
  const [showResponseTemplate, setShowResponseTemplate] = useState(false);

  // Password visibility states
  const [showBearerToken, setShowBearerToken] = useState(false);
  const [showBasicPassword, setShowBasicPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Simple Action ID for API testing - stores the brain_simple_actions.id instead of brain_settings.id
  const [simpleActionId, setSimpleActionId] = useState(null);

  // Handle window resize to detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load available integrations for the user
  useEffect(() => {
    if (isOpen) {
      loadAvailableIntegrations();
      const fetchAccountId = async () => {
        const { accountId: id } = await getUserAccountId();
        setAccountId(id);
      };
      fetchAccountId();
    }
  }, [isOpen]);

  const loadAvailableIntegrations = async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) {
        return;
      }

      // Get user's connected integrations with provider info
      const { data: integrations, error } = await supabase
        .from('accounts_integrations')
        .select(`
          id,
          integration_status,
          is_active,
          api_providers!provider_id (
            id,
            name
          )
        `)
        .eq('account_id', accountId)
        .eq('integration_status', 'complete');

      if (error) throw error;

      // Get credentials for these integrations
      const integrationIds = integrations?.map(i => i.id) || [];
      let credentialsMap = {};
      
      if (integrationIds.length > 0) {
        const { data: credentials, error: credError } = await supabase
          .from('api_credentials')
          .select('integration_id, credential_type')
          .in('integration_id', integrationIds);
          
        if (credError) throw credError;
        
        // Build credentials map by integration ID
        (credentials || []).forEach(cred => {
          if (!credentialsMap[cred.integration_id]) {
            credentialsMap[cred.integration_id] = [];
          }
          credentialsMap[cred.integration_id].push(cred.credential_type);
        });
      }

      // Transform to get unique providers and build credentials mapping
      const providerMap = {};
      const credentialTypesMap = {};
      
      (integrations || []).forEach(integration => {
        const providerId = integration.api_providers.id;

        if (!providerMap[providerId]) {
          providerMap[providerId] = integration.api_providers;
        }
        // Map credential types instead of raw metadata
        credentialTypesMap[providerId] = credentialsMap[integration.id] || [];
      });

      const providers = Object.values(providerMap);

      setAvailableProviders(providers);
      setUserIntegrationsMap(credentialTypesMap);
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  // Handle provider selection
  const handleIntegrationSelection = (providerId) => {
    setSelectedProviderId(providerId);
    
    // Update auth config with provider reference
    setFormData(prev => ({
      ...prev,
      auth_type: 'integration',
      auth_config: {
        api_provider_id: providerId,
        use_integration_auth: true
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Safe JSON parsing function
  const safeJsonParse = (value, defaultValue = {}) => {
    try {
      return typeof value === 'string' ? JSON.parse(value) : value;
    } catch (error) {
      console.warn('Failed to parse JSON:', error);
      return defaultValue;
    }
  };

  // Function to sanitize form data and remove circular references
  const sanitizeFormData = (data) => {
    const sanitized = {};
    
    for (const [key, value] of Object.entries(data)) {
      // Skip DOM elements and functions
      if (value && typeof value === 'object' && value.nodeType) {
        continue; // Skip DOM elements
      }
      if (typeof value === 'function') {
        continue; // Skip functions
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        sanitized[key] = value.map(item => {
          if (item && typeof item === 'object' && item.nodeType) {
            return null; // Skip DOM elements in arrays
          }
          if (typeof item === 'function') {
            return null; // Skip functions in arrays
          }
          return item;
        }).filter(item => item !== null);
      }
      // Handle objects
      else if (value && typeof value === 'object') {
        try {
          // Test if the object can be JSON stringified
          JSON.stringify(value);
          sanitized[key] = value;
        } catch (error) {
          console.warn(`Skipping circular reference in field: ${key}`, error);
          sanitized[key] = null;
        }
      }
      // Handle primitives
      else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  };

  // Format questions for mention components
  const questionsForMention = [
    ...(selectedProviderId && formData.type === 'api' ? [
      {
        id: 'integration_token',
        text: 'integration_token'
      },
      // Add credential variables from selected provider
      ...(Array.isArray(userIntegrationsMap[selectedProviderId]) ? userIntegrationsMap[selectedProviderId] : []).map(credentialType => ({
        id: credentialType,
        text: credentialType
      }))
    ] : []),
    // Add simple action question variables
    ...formData.questions?.map((q, index) => ({
      id: q.id, // Use the actual question ID
      text: `question_${index + 1}_response` // Use the backend format with _response suffix
    })) || []
  ];

  // --- Refactored Data Loading --- 
  const loadActionDetails = async (settingsId) => {
    setModalIsLoading(true);
    try {
      // 0. Fetch Requirements
      const { data: reqData, error: reqError } = await supabase
        .from('brain_setting_requirements')
        .select(`
          *,
          brain_conversation_topics (
            topic_name
          )
        `)
        .eq('settings_id', settingsId);

      if (reqError) {
        console.error("Error fetching requirements", reqError);
      } else {
        setRequirements(reqData || []);
      }

      // 1. Fetch Settings
      const { data: settingsData, error: settingsErr } = await supabase
        .from('brain_settings')
        .select('*')
        .eq('id', settingsId);
      if (settingsErr) throw settingsErr;

      // Check if settings record exists
      if (!settingsData || settingsData.length === 0) {
        console.warn(`No brain_settings record found for ID: ${settingsId}`);
        toast.error("Simple action not found.");
        onClose();
        return;
      }

      const settings = settingsData[0]; // Get first (and should be only) record

      // 2. Fetch Groupings
      const { data: groupingsData, error: groupingsErr } = await supabase
        .from('brain_groupings')
        .select('*')
        .eq('settings_id', settingsId);
      if (groupingsErr) throw groupingsErr;
      const groupings = groupingsData || [];

      // 3. Fetch Location Assignments
      let assignments = {};
      if (groupings.length > 0) {
        const groupIds = groupings.map(g => g.id);
        const { data: locData, error: locErr } = await supabase
          .from('brain_grouping_locations')
          .select('grouping_id, location_id')
          .in('grouping_id', groupIds);
        if (locErr) throw locErr;
        (locData || []).forEach(row => {
          if (!assignments[row.grouping_id]) assignments[row.grouping_id] = [];
          assignments[row.grouping_id].push(row.location_id);
        });
      }
      setLocationGroupings(groupings);
      setLocationAssignments(assignments);

      // 4. Fetch Simple Actions (one per grouping)
      const { data: simpleActionsData, error: saErr } = await supabase
        .from('brain_simple_actions')
        .select('*, grouping_id')
        .eq('settings_id', settingsId);
      if (saErr) throw saErr;
      const simpleActions = simpleActionsData || [];

      // Determine the primary action type (assuming it's consistent across groupings for now)
      const primaryActionType = simpleActions.length > 0 ? simpleActions[0].action_type : '';
      let primaryGroupingId = groupings.length > 0 ? groupings[0].id : null;

      // 5. Fetch Type-Specific Data (Needs to handle multiple groupings)
      const newFormDataByGrouping = {};
      const simpleActionIds = simpleActions.map(sa => sa.id);
      
      // Use Promise.all for efficiency
      let smsData = [], emailData = [], transferData = [], apiData = [], dtmfData = [], noteData = [], questionsData = []; // Added dtmfData, noteData, and questionsData
      let smsNumbers = [], emailAddresses = [], noteFieldData = [], noteEnumData = []; // Added noteFieldData, noteEnumData
      
      if (simpleActionIds.length > 0) {
          const promises = [];
          promises.push(supabase.from('brain_simple_actions_sms').select('*, simple_action_id').in('simple_action_id', simpleActionIds));
          promises.push(supabase.from('brain_simple_actions_email').select('*, simple_action_id').in('simple_action_id', simpleActionIds));
          promises.push(supabase.from('brain_simple_actions_transfer_call').select('*, simple_action_id').in('simple_action_id', simpleActionIds));
          promises.push(supabase.from('brain_simple_actions_api').select('*, simple_action_id').in('simple_action_id', simpleActionIds));
          promises.push(supabase.from('brain_simple_actions_dtmf').select('*, simple_action_id').in('simple_action_id', simpleActionIds)); // Fetch DTMF data
          promises.push(supabase.from('brain_simple_actions_fields').select('*, simple_action_id').in('simple_action_id', simpleActionIds)); // Fetch note fields
          promises.push(supabase.from('brain_simple_actions_questions').select('*, simple_action_id').in('simple_action_id', simpleActionIds)); // Fetch questions data
          
          const [smsRes, emailRes, transferRes, apiRes, dtmfRes, noteRes, questionsRes] = await Promise.all(promises);
          
          smsData = smsRes.data || [];
          emailData = emailRes.data || [];
          transferData = transferRes.data || [];
          apiData = apiRes.data || [];
          dtmfData = dtmfRes.data || []; // Store DTMF data
          noteData = noteRes.data || []; // Store note fields data
          questionsData = questionsRes.data || []; // Store questions data

          // Fetch related numbers/addresses if needed
          const smsActionIds = smsData.map(s => s.id);
          const emailActionIds = emailData.map(e => e.id);
          const noteFieldIds = noteData.map(nf => nf.id); // Get IDs of note fields to fetch enums

          const relatedPromises = [];
          if (smsActionIds.length > 0) relatedPromises.push(supabase.from('brain_simple_actions_sms_numbers').select('*').in('simple_action_sms_id', smsActionIds));
          else relatedPromises.push(Promise.resolve({ data: [] })); 
          if (emailActionIds.length > 0) relatedPromises.push(supabase.from('brain_simple_actions_email_addresses').select('*').in('simple_action_email_id', emailActionIds));
          else relatedPromises.push(Promise.resolve({ data: [] })); 
          if (noteFieldIds.length > 0) relatedPromises.push(supabase.from('brain_simple_actions_fields_enum').select('*').in('simple_action_field_id', noteFieldIds)); // Fetch note enums
          else relatedPromises.push(Promise.resolve({ data: [] }));
          
          const [smsNumRes, emailAddrRes, noteEnumRes] = await Promise.all(relatedPromises);
          smsNumbers = smsNumRes.data || [];
          emailAddresses = emailAddrRes.data || [];
          noteEnumData = noteEnumRes.data || []; // Store note enum data
      }

      // Map data back to groupings
      simpleActions.forEach(sa => {
        const groupingId = sa.grouping_id;
        newFormDataByGrouping[groupingId] = {}; // Initialize entry

        const sms = smsData.find(s => s.simple_action_id === sa.id);
        if (sms) {
          const numbers = smsNumbers.filter(n => n.simple_action_sms_id === sms.id).map(n => n.phone_number);
          newFormDataByGrouping[groupingId].sms_message = sms.message || '';
          newFormDataByGrouping[groupingId].sms_use_contact_phone = sms.use_contact_phone || false;
          newFormDataByGrouping[groupingId].sms_numbers = numbers;
          // newFormDataByGrouping[groupingId].link_url = sms.link_url || ''; // Add if schema supports
        }

        const email = emailData.find(e => e.simple_action_id === sa.id);
        if (email) {
          const addresses = emailAddresses.filter(a => a.simple_action_email_id === email.id).map(a => a.email_address);
          newFormDataByGrouping[groupingId].email_subject = email.subject || '';
          newFormDataByGrouping[groupingId].email_message = email.message || '';
          newFormDataByGrouping[groupingId].email_use_contact_email = email.use_contact_email || false;
          newFormDataByGrouping[groupingId].email_addresses = addresses;
        }

        const transfer = transferData.find(t => t.simple_action_id === sa.id);
        if (transfer) {
          newFormDataByGrouping[groupingId].transfer_name = transfer.name || '';
          newFormDataByGrouping[groupingId].transfer_number = formatTransferNumberForDisplay(transfer.phone_number || '');
          newFormDataByGrouping[groupingId].transfer_start_time = transfer.start_time || '09:00';
          newFormDataByGrouping[groupingId].transfer_end_time = transfer.end_time || '17:00';
          newFormDataByGrouping[groupingId].transfer_days = transfer.days_of_week || [0,1,2,3,4,5,6];
          // newFormDataByGrouping[groupingId].play_message = transfer.play_message || false; // Add if schema supports
          // newFormDataByGrouping[groupingId].transfer_message = transfer.transfer_message || ''; // Add if schema supports
        }

        const api = apiData.find(a => a.simple_action_id === sa.id);
        if (api) {
          newFormDataByGrouping[groupingId].api_endpoint = api.url || '';
          newFormDataByGrouping[groupingId].api_method = api.method || 'POST';
          newFormDataByGrouping[groupingId].api_body = api.body ? JSON.stringify(api.body, null, 2) : ''; // Pretty print JSON
          newFormDataByGrouping[groupingId].api_headers = api.headers || {};
          newFormDataByGrouping[groupingId].api_query_params = api.query_params || {};
          newFormDataByGrouping[groupingId].api_response_template = api.response_template || '';
          newFormDataByGrouping[groupingId].auth_type = api.auth_type || 'none';
          newFormDataByGrouping[groupingId].auth_config = api.auth_config || {};
        }

        const dtmf = dtmfData.find(d => d.simple_action_id === sa.id);
        if (dtmf) {
          newFormDataByGrouping[groupingId].dtmf_number_to_press = dtmf.number_to_press || '';
        }

        // Map Note Fields
        const fieldsForThisAction = noteData.filter(f => f.simple_action_id === sa.id);
        if (fieldsForThisAction.length > 0) {
            newFormDataByGrouping[groupingId].note_fields_config = fieldsForThisAction.map(field => {
                const enums = noteEnumData.filter(e => e.simple_action_field_id === field.id).map(e => e.enum);
                // When loading, the db parent_field_id needs to be findable as a temp_id if we want to re-link in UI
                // For simplicity, we'll handle parent_field_temp_id linkage on the frontend if necessary after all fields are processed
                // Or, assume parent_field_id from DB directly maps to a field.id (which is field.temp_id on load)
                let parentTempId = null;
                if (field.parent_field_id) {
                    // Attempt to find the parent field within the currently loaded fields for THIS simple_action
                    const parentFieldInLoad = fieldsForThisAction.find(f => f.id === field.parent_field_id);
                    if (parentFieldInLoad) parentTempId = parentFieldInLoad.id; // The parent's DB ID becomes its temp_id for UI linking
                }

                return {
                    id: field.id, 
                    temp_id: field.id, // On load, temp_id is the actual db id
                    question: field.question,
                    type: field.type,
                    order: field.order,
                    options: enums,
                    parent_field_id: field.parent_field_id, // Keep original db parent_field_id from DB
                    parent_field_temp_id: parentTempId, // Store the temp_id (which is DB id on load) of the parent for UI linking
                    criteria: field.criteria // Load criteria as is
                };
            }).sort((a, b) => (a.order || 0) - (b.order || 0));
        } else {
            newFormDataByGrouping[groupingId].note_fields_config = [];
        }
      });
      setFormDataByGrouping(newFormDataByGrouping);

      // 6. Populate main formData with primary grouping's data or defaults
      const primaryGroupData = primaryGroupingId ? newFormDataByGrouping[primaryGroupingId] : {};
      
      // Process questions for the primary simple action
      let loadedQuestions = [];
      if (simpleActions.length > 0) {
        const primarySimpleAction = simpleActions.find(sa => sa.grouping_id === primaryGroupingId) || simpleActions[0];
        
        // Store the simple action ID for API testing
        setSimpleActionId(primarySimpleAction.id);
        
        loadedQuestions = questionsData
          .filter(q => q.simple_action_id === primarySimpleAction.id)
          .sort((a, b) => a.order - b.order)
          .map(q => ({
            id: q.id,
            question: q.question,
            type: q.type,
            order: q.order
          }));
      }
      
      setFormData(prev => ({
        ...prev, // Keep ID if passed initially
        settings_id: settingsId, // Store settings_id
        name: settings.name || '',
        description: settings.description || '',
        triggers: Array.isArray(settings.triggers) ? [...settings.triggers] : [],
        type: primaryActionType,
        category_id: settings.category_id || '',
        questions: loadedQuestions, // Set loaded questions
        showLocationVariants: groupings.length > 1, // Enable toggle if more than 1 grouping exists
        
        // SMS fields (from primary grouping or defaults)
        sms_message: primaryGroupData?.sms_message || '',
        sms_numbers: primaryGroupData?.sms_numbers || [],
        sms_use_contact_phone: primaryGroupData?.sms_use_contact_phone || false,
        // link_url: primaryGroupData?.link_url || '', // Add if schema supports
        
        // Email fields
        email_subject: primaryGroupData?.email_subject || '',
        email_message: primaryGroupData?.email_message || '',
        email_addresses: primaryGroupData?.email_addresses || [],
        email_use_contact_email: primaryGroupData?.email_use_contact_email || false,
        
        // Transfer Call fields
        transfer_number: primaryGroupData?.transfer_number || '',
        transfer_name: primaryGroupData?.transfer_name || '',
        transfer_days: primaryGroupData?.transfer_days || [0,1,2,3,4,5,6],
        transfer_start_time: primaryGroupData?.transfer_start_time || '09:00',
        transfer_end_time: primaryGroupData?.transfer_end_time || '17:00',
        // play_message: primaryGroupData?.play_message || false, // Add if schema supports
        // transfer_message: primaryGroupData?.transfer_message || '', // Add if schema supports
        
        // API fields
        api_endpoint: primaryGroupData?.api_endpoint || '',
        api_method: primaryGroupData?.api_method || 'POST',
        api_body: primaryGroupData?.api_body || '',
        api_headers: primaryGroupData?.api_headers || {},
        api_query_params: primaryGroupData?.api_query_params || {},
        api_response_template: primaryGroupData?.api_response_template || '',
        auth_type: primaryGroupData?.auth_type || 'none',
        auth_config: primaryGroupData?.auth_config || {},

        // DTMF fields
        dtmf_number_to_press: primaryGroupData?.dtmf_number_to_press || '',

        // Note fields
        note_fields_config: primaryGroupData?.note_fields_config || []
      }));

      // Set active grouping
      setActiveLocationGrouping(primaryGroupingId);
      
      // Set questions toggle based on loaded questions
      setShowQuestions(loadedQuestions.length > 0);

      // Initialize integration auth state if needed
      if (primaryGroupData?.auth_type === 'integration' && primaryGroupData?.auth_config?.api_provider_id) {
        setUseIntegrationAuth(true);
        setSelectedProviderId(primaryGroupData.auth_config.api_provider_id);
        
        const { getUserAccountId } = await import('../../utils/accountUtils');
        const { accountId } = await getUserAccountId();

        // Load metadata for this provider from user's integrations
        const { data: integrationData, error: integrationError } = await supabase
          .from('accounts_integrations')
          .select('metadata')
          .eq('account_id', accountId)
          .eq('provider_id', primaryGroupData.auth_config.api_provider_id)
          .eq('is_active', true)
          .eq('integration_status', 'complete')
          .single();
        
        if (!integrationError && integrationData) {
          setUserIntegrationsMap(prev => ({ ...prev, [primaryGroupData.auth_config.api_provider_id]: integrationData.metadata || {} }));
        }
      }

    } catch (error) {
      // console.error("Error loading action details:", error); // Keep this important one if you want
      toast.error("Failed to load action details.");
    } finally {
      setModalIsLoading(false);
      setHasUnsavedChanges(false); // Reset after loading
    }
  };
  // --- End Refactored Data Loading --- 

  // Fetch categories when the modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Initialize form with action data when opened
  useEffect(() => {
    if (isOpen) {
      // Reset states for new/reload
      setFormData({
        name: '', description: '', triggers: [], is_active: true, type: '',
        category_id: preSelectedCategoryId || '', showLocationVariants: false,
        all_locations: false, // Initialize all_locations here as well
        selected_locations: [], // Initialize selected_locations here as well
        questions: [], // Initialize questions array
        sms_message: '', sms_numbers: [], sms_use_contact_phone: false,
        email_subject: '', email_message: '', email_addresses: [], email_use_contact_email: false,
        transfer_number: '', transfer_name: '', transfer_days: [0,1,2,3,4,5,6], transfer_start_time: '09:00', transfer_end_time: '17:00',
        api_endpoint: '', api_method: 'POST', api_body: '', api_headers: {}, api_query_params: {}, api_response_template: '', auth_type: 'none', auth_config: {},
        dtmf_number_to_press: '',
        note_fields_config: [] // Initialize note_fields_config
      });
      setLocationGroupings([]);
      setLocationAssignments({});
      setFormDataByGrouping({});
      setActiveLocationGrouping(null);
      setValidationErrors({});
      setHasUnsavedChanges(false);
      setActiveTab("details");
      
      // Reset simple action ID
      setSimpleActionId(null);
      
      // OPTIMIZATION: Skip loading states when data is pre-loaded
      setModalIsLoading(!isPreLoaded);

      if (action && action.settings_id && !isNew && !isPreLoaded) {
        // Use action.settings_id, which is the correct reference to the brain_settings table.
        // The 'action' prop is a composite object.
        loadActionDetails(action.settings_id); // Load data if editing
      } else if (isPreLoaded && action) {
        // OPTIMIZATION: Use pre-loaded data instantly
        setFormData(prev => ({
          ...prev,
          ...action,
          settings_id: action.settings_id,
          name: action.name || '',
          description: action.description || '',
          triggers: Array.isArray(action.triggers) ? [...action.triggers] : [],
          type: action.tool_type || action.type || '', // Map tool_type to type
          category_id: action.category_id || '',
          // Map additional fields from VoiceUploadModal
          transfer_number: action.transfer_number || '',
          transfer_name: action.transfer_name || '',
          note_fields_config: action.note_fields_config || []
        }));
        
        if (action.locationGroupings) setLocationGroupings(action.locationGroupings);
        if (action.locationAssignments) setLocationAssignments(action.locationAssignments);
        if (action.formDataByGrouping) setFormDataByGrouping(action.formDataByGrouping);
        if (action.mainGroupingId) setActiveLocationGrouping(action.mainGroupingId);
        
        setModalIsLoading(false);
      } else {
        // Setup for a completely new action
        setModalIsLoading(false);
        if (isNew) {
          // If explicitly creating new, maybe open type selector? 
          // Or leave type blank as it is.
        }
      }

      initializedRef.current = true;
    } else {
      // Reset when modal closes
      initializedRef.current = false;
    }
  }, [isOpen, action?.settings_id, isNew, isPreLoaded, preSelectedCategoryId]); // Added preSelectedCategoryId to dependencies

  // Add voice form filling event listener
  useEffect(() => {
    if (!isOpen) return;

    const handleVoiceFormFill = (event) => {
      const { fieldName, value, modalType } = event.detail;
      
      // Only handle events for this modal type or universal events
      if (modalType && modalType !== 'simple_action') return;
      
      // Map voice field names to form fields based on current action type
      const getFieldMapping = () => {
        const baseMapping = {
          'name': 'name',
          'tool name': 'name',
          'title': 'name',
          'description': 'description'
        };

        // Add type-specific mappings based on current action type
        if (formData.type === 'sms') {
          return {
            ...baseMapping,
            'message': 'sms_message',
            'text': 'sms_message',
            'sms message': 'sms_message',
            'text message': 'sms_message'
          };
        } else if (formData.type === 'email') {
          return {
            ...baseMapping,
            'subject': 'email_subject',
            'email subject': 'email_subject',
            'message': 'email_message',
            'email message': 'email_message',
            'body': 'email_message',
            'content': 'email_message'
          };
        } else if (formData.type === 'transfer') {
          return {
            ...baseMapping,
            'phone number': 'transfer_number',
            'number': 'transfer_number',
            'transfer number': 'transfer_number',
            'transfer name': 'transfer_name',
            'department': 'transfer_name'
          };
        } else if (formData.type === 'api') {
          return {
            ...baseMapping,
            'endpoint': 'api_endpoint',
            'url': 'api_endpoint',
            'api endpoint': 'api_endpoint',
            'body': 'api_body',
            'api body': 'api_body',
            'request body': 'api_body',
            'payload': 'api_body'
          };
        } else if (formData.type === 'dtmf') {
          return {
            ...baseMapping,
            'number': 'dtmf_number_to_press',
            'digits': 'dtmf_number_to_press',
            'dtmf number': 'dtmf_number_to_press',
            'number to press': 'dtmf_number_to_press'
          };
        }
        
        return baseMapping;
      };
      
      const fieldMapping = getFieldMapping();
      const normalizedFieldName = fieldName.toLowerCase().trim();
      const actualFieldName = fieldMapping[normalizedFieldName] || normalizedFieldName;
      
      // Update the appropriate field
      const validFields = Object.values(fieldMapping);
      
      if (validFields.includes(actualFieldName)) {
        if (formData.showLocationVariants && ['sms_message', 'email_subject', 'email_message', 'transfer_number', 'transfer_name', 'api_endpoint', 'api_body', 'api_headers', 'api_query_params', 'dtmf_number_to_press'].includes(actualFieldName)) {
          // Handle location variants for action-specific fields
          handleFormChangeWithVariants({ 
            target: { name: actualFieldName, value: value }
          });
        } else {
          // Handle regular form fields
          setFormData(prev => ({
            ...prev,
            [actualFieldName]: value
          }));
          setHasUnsavedChanges(true);
          
          // Clear validation error
          if (validationErrors[actualFieldName]) {
            setValidationErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[actualFieldName];
              return newErrors;
            });
          }
        }
        
        console.log('✅ Simple Action field updated via voice:', actualFieldName);
      } else {
        console.warn('Unknown field name for Simple Action:', fieldName);
      }
    };

    window.addEventListener('fillFormField', handleVoiceFormFill);
    
    return () => {
      window.removeEventListener('fillFormField', handleVoiceFormFill);
    };
  }, [isOpen, formData.type, formData.showLocationVariants, validationErrors]);

  // Add voice command listener for selecting a tool type
  useEffect(() => {
    if (!isOpen) return;

    const handleSelectToolType = (event) => {
      const { toolType } = event.detail;
      console.log('🎤 Voice command to select tool type:', toolType);

      // Map spoken phrases to tool IDs
      const toolTypeMapping = {
        'sms': 'sms', 'text': 'sms', 'text message': 'sms',
        'email': 'email',
        'transfer': 'transfer', 'call transfer': 'transfer', 'phone transfer': 'transfer',
        'api': 'api',
        'dtmf': 'dtmf',
        'note': 'note', 'take note': 'note'
      };

      const normalizedToolType = toolType.toLowerCase().trim();
      const mappedToolType = toolTypeMapping[normalizedToolType];

      if (mappedToolType) {
        handleToolTypeSelect(mappedToolType);
        console.log(`✅ Tool type changed to: ${mappedToolType}`);
      } else {
        console.warn(`Unknown tool type requested via voice: "${toolType}"`);
      }
    };
    
    window.addEventListener('selectToolType', handleSelectToolType);
    
    return () => {
      window.removeEventListener('selectToolType', handleSelectToolType);
    };
  }, [isOpen]);

  // Add fetchCategories function
  const fetchCategories = async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId, error: accountIdError } = await getUserAccountId();
      if (accountIdError || !accountId) throw new Error(accountIdError || 'Account ID not found');
      
      // Fetch categories for the current account
      const { data, error } = await supabase
        .from('brain_categories')
        .select('id, name')
        .eq('account_id', accountId)
        .order('name');
        
      if (error) throw error;
      
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  // Auto-select newly created category
  useEffect(() => {
    if (newlyCreatedCategory && isOpen) {
      // Add the category to the local list if it's not already there
      setCategories(prev => {
        const exists = prev.some(cat => cat.id === newlyCreatedCategory.id);
        if (!exists) {
          return [...prev, newlyCreatedCategory];
        }
        return prev;
      });
      
      // Select the new category
      setFormData(prev => ({
        ...prev,
        category_id: newlyCreatedCategory.id
      }));
      
      // Clear validation error for category
      if (validationErrors.category_id) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.category_id;
          return newErrors;
        });
      }
      
      setHasUnsavedChanges(true);
      
      // Clear the newly created category after using it
      if (onClearNewlyCreatedCategory) {
        onClearNewlyCreatedCategory();
      }
    }
  }, [newlyCreatedCategory, isOpen]);

  // Add handleCategoryChange function
  const handleCategoryChange = (value) => {
    if (value === 'new') {
      if (onCreateCategory) {
        onCreateCategory();
      }
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      category_id: value
    }));
    
    // Clear validation error for category
    if (validationErrors.category_id) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.category_id;
        return newErrors;
      });
    }
    
    setHasUnsavedChanges(true);
  };
  
  // Add handleNewCategoryCreated function
  const handleNewCategoryCreated = (newCategory) => {
    // Add the new category to the list
    setCategories(prev => [...prev, newCategory]);
    
    // Select the new category
    setFormData(prev => ({
      ...prev,
      category_id: newCategory.id
    }));
    
    // Clear any validation errors
    if (validationErrors.category_id) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.category_id;
        return newErrors;
      });
    }
    
    setHasUnsavedChanges(true);
    setIsNewCategoryModalOpen(false);
  };

  // Handle field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    // Sanitize the value to prevent circular references
    const sanitizedValue = typeof newValue === 'object' ? sanitizeFormData({ temp: newValue }).temp : newValue;
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setHasUnsavedChanges(true);
  };

  // Handle select changes
  const handleSelectChange = (value, name) => {
    // Sanitize the value to prevent circular references
    const sanitizedValue = typeof value === 'object' ? sanitizeFormData({ temp: value }).temp : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setHasUnsavedChanges(true);
  };

  // Handle trigger keyword changes
  const handleAddTrigger = (e) => {
    e.preventDefault();
    
    if (!currentTrigger.trim()) return;
    
    const newTrigger = currentTrigger.trim().toLowerCase();
    
    // Check if trigger already exists
    if (formData.triggers.includes(newTrigger)) {
      // Don't add duplicate triggers
      setCurrentTrigger('');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      triggers: [...prev.triggers, newTrigger]
    }));
    
    // Clear trigger input
    setCurrentTrigger('');
    
    // Clear validation error for triggers
    if (validationErrors.triggers) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.triggers;
        return newErrors;
      });
    }
    
    setHasUnsavedChanges(true);
  };

  // Handle trigger removal
  const handleRemoveTrigger = (triggerToRemove) => {
    setFormData(prev => ({
      ...prev,
      triggers: prev.triggers.filter(t => t !== triggerToRemove)
    }));
    
    setHasUnsavedChanges(true);
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    // Required fields for all types
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (formData.triggers.length === 0) {
      errors.triggers = "At least one keyword trigger is required";
    }
    
    // Add category validation
    if (!formData.category_id) {
      errors.category_id = "Category is required";
    }
    
    // Location validation - require either all_locations or at least one selected location
    if (!formData.all_locations && formData.selected_locations.length === 0 && locations.length > 0) {
      errors.selected_locations = "At least one location must be selected";
    }
    
    // Only validate action-related fields if action tab is visible
    if (!hideActionTab) {
    // Require a tool type to be selected
    if (!formData.type) {
      errors.type = "Tool type is required";
      return errors; // Exit early if no type is selected
    }
    
    // Type-specific validations
    if (formData.type === 'sms') {
      if (!formData.sms_message.trim()) {
        errors.sms_message = "SMS message is required";
      }
      
      // SMS message length validation
      if (formData.sms_message.length > 160) {
        errors.sms_message = "SMS message must be 160 characters or less";
      }
      
      // Only validate sms_numbers if use_contact_phone is true
      if (formData.sms_use_contact_phone && formData.sms_numbers.length === 0) {
        errors.sms_numbers = "At least one phone number recipient is required";
      }
    }
    else if (formData.type === 'email') {
      if (!formData.email_subject.trim()) {
        errors.email_subject = "Email subject is required";
      }
      
      if (!formData.email_message.trim()) {
        errors.email_message = "Email message is required";
      }
      
      // Only validate email_addresses if use_contact_email is true
      if (formData.email_use_contact_email && formData.email_addresses.length === 0) {
        errors.email_addresses = "At least one email recipient is required";
      }
    }
    else if (formData.type === 'transfer') {
      if (!formData.transfer_name?.trim()) {
        errors.transfer_name = "Transfer name is required";
      }
      
      if (!formData.transfer_number?.trim()) {
        errors.transfer_number = "Transfer number is required";
      }
      
      if (!formData.transfer_start_time) {
        errors.transfer_start_time = "Start time is required";
      }
      
      if (!formData.transfer_end_time) {
        errors.transfer_end_time = "End time is required";
      }
      
      if (formData.transfer_days.length === 0) {
        errors.transfer_days = "At least one day of week must be selected";
      }
    }
    else if (formData.type === 'api') {
      if (!formData.api_endpoint?.trim()) {
        errors.api_endpoint = "API endpoint is required";
      }
      
      // For POST, PUT, DELETE methods, require a body
      if (['POST', 'PUT', 'DELETE'].includes(formData.api_method) && !formData.api_body?.trim()) {
        errors.api_body = "Request body is required for " + formData.api_method + " requests";
      }
      
      // Validate API body if present (must be valid JSON)
      if (formData.api_body?.trim()) {
        try {
          JSON.parse(formData.api_body);
        } catch (e) {
          errors.api_body = "Request body must be valid JSON";
        }
      }
    }
    else if (formData.type === 'dtmf') {
      if (!formData.dtmf_number_to_press?.trim()) {
        errors.dtmf_number_to_press = "Number to press is required";
      }
      
      // Validate that it only contains valid DTMF characters (0-9, *, #)
      if (formData.dtmf_number_to_press?.trim() && !/^[0-9*#]+$/.test(formData.dtmf_number_to_press.trim())) {
        errors.dtmf_number_to_press = "Only numbers (0-9), *, and # are allowed";
      }
    }
    else if (formData.type === 'note') {
      if (!formData.note_fields_config || formData.note_fields_config.length === 0) {
        errors.note_fields_config = "At least one note field is required";
      } else {
        formData.note_fields_config.forEach((field, index) => {
          if (!field.question || !field.question.trim()) {
            if (!errors.note_fields_individual) errors.note_fields_individual = {};
            errors.note_fields_individual[field.temp_id || `new_${index}`] = "Field label is required";
          }
          if ((field.type === 'radio' || field.type === 'select') && (!field.options || field.options.length === 0)) {
            if (!errors.note_fields_individual) errors.note_fields_individual = {};
            errors.note_fields_individual[field.temp_id || `new_${index}`] = "At least one option is required for this field type";
          }
        });
        if (errors.note_fields_individual && Object.keys(errors.note_fields_individual).length > 0) {
            // Set a general error as well to indicate issues in the note fields tab
            errors.note_fields_tab_error = "Please fix errors in note fields."
          }
        }
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  ////////////////////////////////
  // Helper to create type-specific rows //
  ////////////////////////////////
    const createTypeSpecificRows = async (simpleActionId, isVariant = false, groupSpecificData = null) => {
            const currentFormData = isVariant && groupSpecificData ? groupSpecificData : formData;
      
      // Sanitize the form data to prevent circular references
      const sanitizedFormData = sanitizeFormData(currentFormData);

      if (sanitizedFormData.type === 'api') {
        const { error: apiErr } = await supabase
          .from('brain_simple_actions_api')
          .upsert({
            simple_action_id: simpleActionId,
            url: sanitizedFormData.api_endpoint,
            method: sanitizedFormData.api_method,
            headers: Object.keys(sanitizedFormData.api_headers || {}).length ? sanitizedFormData.api_headers : null,
            api_query_params: sanitizedFormData.api_query_params || {},
            body: sanitizedFormData.api_body?.trim() ? JSON.parse(sanitizedFormData.api_body) : null,
            response_template: sanitizedFormData.api_response_template || '',
            auth_type: sanitizedFormData.auth_type || 'none',
            auth_config: sanitizedFormData.auth_config || {}
          }, { onConflict: 'simple_action_id' });
        if (apiErr) throw apiErr;
      }
      else if (sanitizedFormData.type === 'email') {
        const { data: emailRow, error: emailErr } = await supabase
          .from('brain_simple_actions_email')
          .upsert({
            simple_action_id: simpleActionId,
            subject: sanitizedFormData.email_subject,
            message: sanitizedFormData.email_message,
            use_contact_email: sanitizedFormData.email_use_contact_email
          }, { onConflict: 'simple_action_id' })
          .select()
          .single();
        if (emailErr) throw emailErr;

        // Delete existing email addresses and insert new ones
        await supabase
          .from('brain_simple_actions_email_addresses')
          .delete()
          .eq('simple_action_email_id', emailRow.id);

        if (sanitizedFormData.email_use_contact_email && sanitizedFormData.email_addresses.length) {
          const addrRecords = sanitizedFormData.email_addresses.map((addr) => ({
            simple_action_email_id: emailRow.id,
            email_address: addr
          }));
          const { error: addrErr } = await supabase
            .from('brain_simple_actions_email_addresses')
            .insert(addrRecords);
          if (addrErr) throw addrErr;
        }
      }
      else if (sanitizedFormData.type === 'sms') {
        const { data: smsRow, error: smsErr } = await supabase
          .from('brain_simple_actions_sms')
          .upsert({
            simple_action_id: simpleActionId,
            message: sanitizedFormData.sms_message,
            use_contact_phone: sanitizedFormData.sms_use_contact_phone
            // link_url: sanitizedFormData.link_url, // Add if schema supports
          }, { onConflict: 'simple_action_id' })
          .select()
          .single();
        if (smsErr) throw smsErr;

        // Delete existing SMS numbers and insert new ones
        await supabase
          .from('brain_simple_actions_sms_numbers')
          .delete()
          .eq('simple_action_sms_id', smsRow.id);

        if (sanitizedFormData.sms_use_contact_phone && sanitizedFormData.sms_numbers.length) {
          const numRecords = sanitizedFormData.sms_numbers.map((num) => ({
            simple_action_sms_id: smsRow.id,
            phone_number: num
          }));
          const { error: numErr } = await supabase
            .from('brain_simple_actions_sms_numbers')
            .insert(numRecords);
          if (numErr) throw numErr;
        }
      }
      else if (sanitizedFormData.type === 'transfer') {
        const { error: trErr } = await supabase
          .from('brain_simple_actions_transfer_call')
          .upsert({
            simple_action_id: simpleActionId,
            phone_number: convertToE164(sanitizedFormData.transfer_number),
            start_time: sanitizedFormData.transfer_start_time,
            end_time: sanitizedFormData.transfer_end_time,
            days_of_week: sanitizedFormData.transfer_days,
            name: sanitizedFormData.transfer_name
          }, { onConflict: 'simple_action_id' });
        if (trErr) throw trErr;
      }
      else if (sanitizedFormData.type === 'dtmf') {
        const { error: dtmfErr } = await supabase
          .from('brain_simple_actions_dtmf')
          .upsert({
            simple_action_id: simpleActionId,
            number_to_press: sanitizedFormData.dtmf_number_to_press
          }, { onConflict: 'simple_action_id' });
        if (dtmfErr) throw dtmfErr;
      }
      else if (sanitizedFormData.type === 'note') {
        const noteFieldsToSave = sanitizedFormData.note_fields_config || [];
        
        // Delete existing note fields for this simple_action_id before inserting new ones
        const { error: deleteErr } = await supabase
          .from('brain_simple_actions_fields')
          .delete()
          .eq('simple_action_id', simpleActionId);
        if (deleteErr) throw deleteErr;

        if (noteFieldsToSave.length > 0) {
          const tempIdToDbIdMap = {};

          // Pass 1: Insert core field data (without parent_field_id and criteria initially)
          // Ensure `order` is correctly derived if not already set perfectly on field objects
          const orderedFieldsToSave = noteFieldsToSave.map((f, idx) => ({ ...f, order: f.order ?? idx }));

          for (const field of orderedFieldsToSave) {
            const { data: fieldRow, error: fieldErr } = await supabase
              .from('brain_simple_actions_fields')
              .insert({
                simple_action_id: simpleActionId,
                question: field.question,
                type: field.type, 
                order: field.order
              })
              .select('id')
              .single();
            if (fieldErr) { console.error("Error inserting field (create)", field, fieldErr); throw fieldErr; }
            tempIdToDbIdMap[field.temp_id] = fieldRow.id;

            if (field.type === 'enum' && field.options && field.options.length > 0) {
              const enumRecords = field.options.map(opt => ({
                simple_action_field_id: fieldRow.id,
                enum: opt
              }));
              const { error: enumErr } = await supabase
                .from('brain_simple_actions_fields_enum')
                .insert(enumRecords);
              if (enumErr) { console.error("Error inserting enums (create)", field, enumErr); throw enumErr; }
            }
          }

          // Pass 2: Update parent_field_id and criteria
          for (const field of orderedFieldsToSave) {
            const currentDbId = tempIdToDbIdMap[field.temp_id];
            if (!currentDbId) {
                console.warn("Could not find DB ID for field in Pass 2 (create):", field);
                continue; // Should not happen if Pass 1 succeeded for this field
            }

            let parentDbIdToSave = null;
            if (field.parent_field_temp_id) {
                parentDbIdToSave = tempIdToDbIdMap[field.parent_field_temp_id];
                if (!parentDbIdToSave) {
                    console.warn(`Could not resolve parent_field_id for field ${field.question} (parent_temp_id: ${field.parent_field_temp_id}) during create.`);
                }
            }

            // Only update if there's a parent or criteria to set
            if (parentDbIdToSave || field.criteria) {
                const updatePayload = {};
                if (parentDbIdToSave) updatePayload.parent_field_id = parentDbIdToSave;
                if (field.criteria) updatePayload.criteria = field.criteria;

                if (Object.keys(updatePayload).length > 0) {
                    const { error: updateErr } = await supabase
                        .from('brain_simple_actions_fields')
                        .update(updatePayload)
                        .eq('id', currentDbId);
                    if (updateErr) { console.error("Error updating parent/criteria (create)", field, updateErr); throw updateErr; }
                }
            }
          }
        }
      }
    };

  ////////////////////////////////
  // NEW: createNewTool helper  //
  ////////////////////////////////
  const createNewTool = async () => {
    // Step 1: create brain_settings row
    const { data: settingsRow, error: settingsError } = await supabase
      .from('brain_settings')
      .insert({
        category_id: formData.category_id,
        type: 'simple_action',
        name: formData.name,
        description: formData.description || null,
        triggers: formData.triggers && formData.triggers.length ? formData.triggers : null,
        rules: null
      })
      .select()
      .single();
    if (settingsError) throw settingsError;

    const settingsId = settingsRow.id;

    // Determine the groupings to save
    let groupsToSave = [];
    if (formData.showLocationVariants) {
      groupsToSave = locationGroupings.length ? [...locationGroupings] : [];
    }
    if (!groupsToSave.length) {
      // Always create at least one grouping
      groupsToSave = [{ id: 'default', name: 'Default' }];
    }

    // Map of original grouping id -> new db id
    const groupingIdMap = {};

    // Step 2: insert groupings
    for (const grp of groupsToSave) {
      const { data: groupRow, error: groupErr } = await supabase
        .from('brain_groupings')
        .insert({ 
          settings_id: settingsId, 
          name: grp.name || 'Default'
        })
        .select()
        .single();
      if (groupErr) throw groupErr;
      groupingIdMap[grp.id] = groupRow.id;
    }

    // Step 3: insert grouping -> location assignments
    const groupingLocationRecords = [];
    for (const grp of groupsToSave) {
      const assignedLocIds = formData.showLocationVariants
        ? (locationAssignments[grp.id] || [])
        : (formData.all_locations ? [] : formData.selected_locations);

      if (assignedLocIds && assignedLocIds.length) {
        assignedLocIds.forEach((locId) => {
          groupingLocationRecords.push({
            grouping_id: groupingIdMap[grp.id],
            location_id: locId
          });
        });
      }
    }
    if (groupingLocationRecords.length) {
      const { error: glErr } = await supabase
        .from('brain_grouping_locations')
        .insert(groupingLocationRecords);
      if (glErr) throw glErr;
    }
    
    // Loop through groupsToSave (this part handles variants correctly)
    for (const grp of groupsToSave) {
        const dbGroupId = groupingIdMap[grp.id];
        const { data: saRow, error: saErr } = await supabase
            .from('brain_simple_actions')
            .insert({
                settings_id: settingsId,
                grouping_id: dbGroupId, // Use the actual DB grouping ID
                action_type: formData.type // Use the primary formData.type
            })
            .select()
            .single();
        if (saErr) throw saErr;

        // Determine which data to use for type-specific rows
        let dataForTypeSpecifics = formData; // Default to main form data
        if (formData.showLocationVariants && formDataByGrouping[grp.id]) {
            // If variants are enabled and this group has specific data, use it
            // but merge with main form data to ensure all base fields are present
             dataForTypeSpecifics = {
                ...formData, // Start with base formData (name, description, category, etc.)
                ...formDataByGrouping[grp.id] // Override with group-specific action fields
            };
             // Ensure the 'type' from the main formData is respected for consistency, 
             // as action_type is not per-variant for simple actions
            dataForTypeSpecifics.type = formData.type;
        }
        await createTypeSpecificRows(saRow.id, formData.showLocationVariants, dataForTypeSpecifics);
        
        // Save questions for this simple action if any exist
        if (formData.questions && formData.questions.length > 0) {
          await saveSimpleActionQuestions(saRow.id, formData.questions);
        }
    }

    // Return the formatted setting data that matches fetchCategories structure
    const formattedSetting = {
      ...settingsRow,
      original_type: settingsRow.type,
      fullData: {
        id: null, // Simple action row ID would go here, but not needed for sidebar display
        settings_id: settingsId,
        name: settingsRow.name,
        description: settingsRow.description || '',
        triggers: settingsRow.triggers || [],
        type: formData.type,
        category_id: settingsRow.category_id,
      },
      action_type: formData.type, // Extract action_type for sidebar display
      locationVariants: [] // Start with empty variants, will be populated when fetched
    };

    return formattedSetting;
  };

  // --- Define Helper Function HERE ---
  // Helper function to delete data from other action types when type changes
  const deleteOrphanedActionData = async (supabase, simpleActionId, typeToKeep) => {
    const allTypes = ['api', 'email', 'sms', 'transfer', 'dtmf', 'note']; // Added 'dtmf' and 'note'
    const typesToDelete = allTypes.filter(t => t !== typeToKeep);
  
    for (const type of typesToDelete) {
      let tableName = null;
      let isNoteType = false;
      switch (type) {
        case 'api':      tableName = 'brain_simple_actions_api'; break;
        case 'email':    tableName = 'brain_simple_actions_email'; break;
        case 'sms':      tableName = 'brain_simple_actions_sms'; break;
        case 'transfer': tableName = 'brain_simple_actions_transfer_call'; break;
        case 'dtmf':     tableName = 'brain_simple_actions_dtmf'; break;
        case 'note':     isNoteType = true; break; // Special handling for note and its fields
        default: continue; 
      }
  
      if (isNoteType) {
        // For 'note', we need to delete from brain_simple_actions_fields
        // CASCADE on brain_simple_actions_fields should handle brain_simple_actions_fields_enum
        const { error: noteFieldsError } = await supabase
          .from('brain_simple_actions_fields')
          .delete()
          .eq('simple_action_id', simpleActionId);
        if (noteFieldsError) {
          console.error(`Error deleting orphaned data from brain_simple_actions_fields for simpleActionId ${simpleActionId}:`, noteFieldsError);
        }
      } else if (tableName) {
        const { error } = await supabase
          .from(tableName)
          .delete()
          .eq('simple_action_id', simpleActionId);
        
        if (error) {
          console.error(`Error deleting orphaned data from ${tableName} for simpleActionId ${simpleActionId}:`, error);
        }
        // Note: CASCADE constraints should handle related tables like _email_addresses, _sms_numbers
      }
    }
  };
  // --- End Helper Function Definition ---

  // Helper function to save simple action questions
  const saveSimpleActionQuestions = async (simpleActionId, questions) => {
    // Delete existing questions for this simple action
    const { error: deleteErr } = await supabase
      .from('brain_simple_actions_questions')
      .delete()
      .eq('simple_action_id', simpleActionId);
    if (deleteErr) throw deleteErr;

    // Insert new questions if any exist
    if (questions && questions.length > 0) {
      const questionsToInsert = questions.map((question, index) => ({
        simple_action_id: simpleActionId,
        order: index + 1, // Simple: just use array position
        question: question.question,
        type: question.type
      }));

      const { error: insertErr } = await supabase
        .from('brain_simple_actions_questions')
        .insert(questionsToInsert);
      if (insertErr) throw insertErr;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      // If type is not selected, open the tool type modal
      if (validationErrors.type) {
        setIsToolTypeModalOpen(true);
        toast.error("Please select a tool type");
        return;
      }
      
      // If there are errors, switch to the tab containing the first error
      if (validationErrors.name || validationErrors.description || validationErrors.category_id) {
        setActiveTab("details");
      } else if (validationErrors.triggers) {
        setActiveTab("triggers");
      } else if (
        Object.keys(validationErrors).some(k => k.startsWith('sms_') || k.startsWith('email_') || k.startsWith('transfer_') || k.startsWith('api_') || k.startsWith('dtmf_'))
      ) {
        setActiveTab("message");
      }
      else if (validationErrors.note_fields_config || validationErrors.note_fields_tab_error) {
        setActiveTab("note_fields"); // Switch to new note tab if errors there
      }
      
      toast.error("Please fix the errors before saving");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Check if this is a real database ID or a temporary ID
      const isExistingRecord = action && action.settings_id && !action.settings_id.toString().startsWith('add-') && !action.settings_id.toString().startsWith('edit-') && !action.settings_id.toString().startsWith('new-');
      
      let settingsId;

      if (isExistingRecord) { // UPDATE: Check for real settings_id
        settingsId = action.settings_id;

        // 1. Update brain_settings
        await supabase
          .from('brain_settings')
          .update({
          name: formData.name,
          description: formData.description,
            category_id: formData.category_id,
          triggers: formData.triggers,
          })
          .eq('id', settingsId);

        // 2. Update the data for the ACTIVE grouping ONLY (only if action tab is visible).
        // Location assignments are managed in a separate modal, so we only save content here.
        if (!hideActionTab) {
          if (activeLocationGrouping) {
              // The main `formData` state holds the data for the active variant,
              // so we can use that directly for the save payload.
              const groupDataForSave = { ...formData };
              
              // A. Upsert brain_simple_actions row to ensure it exists for this grouping
            const { data: saRow, error: saErr } = await supabase
                .from('brain_simple_actions')
                .upsert({ 
                    settings_id: settingsId, 
                    grouping_id: activeLocationGrouping,
                    action_type: groupDataForSave.type
                }, { onConflict: ['settings_id', 'grouping_id'], ignoreDuplicates: false }) // Correct onConflict syntax
                .select('id')
                .single();

            if (saErr) throw saErr;
            const simpleActionId = saRow.id;

              // B. Delete data from other action types if type has changed for this variant
              await deleteOrphanedActionData(supabase, simpleActionId, groupDataForSave.type);

              // C. Upsert type-specific data for this variant.
              await createTypeSpecificRows(simpleActionId, true, groupDataForSave);

              // D. Save questions if any exist
              if (formData.questions && formData.questions.length > 0) {
                await saveSimpleActionQuestions(simpleActionId, formData.questions);
              } else {
                // Delete existing questions if none are provided
                await supabase
                  .from('brain_simple_actions_questions')
                  .delete()
                  .eq('simple_action_id', simpleActionId);
              }

                } else {
              console.error("No active location grouping found during update.");
              toast.error("Could not save, no active location selected.");
              throw new Error("Cannot update action without an active grouping.");
                      }
        }

        toast.success("Tool updated successfully");
        runAutomaticConflictDetection(supabase);
        
        // **OPTIMISTIC UI UPDATE**: Update parent categories state immediately
        if (onUpdateSetting) {
          onUpdateSetting(settingsId, {
            name: formData.name,
            category_id: formData.category_id,
            original_type: 'simple_action', // Ensure original_type is passed for correct display
            action_type: formData.type // Pass the specific action type for correct display
          });
        }

        // Pass the settings ID for coverage analysis
        const updateResult = { success: true, settingsId: settingsId, isUpdate: true };
        
        // NEW: Always call onClose for UPDATE operations (to trigger coverage refresh in Brain.js)
        initializedRef.current = false;
        setHasUnsavedChanges(false);
        setHasLocationChanges(false);
        onClose(updateResult); // Pass settings ID to trigger coverage analysis
        
      } else {
        // CREATE Logic (only if action tab is visible)
        let newSettingData = null;
        if (!hideActionTab) {
          newSettingData = await createNewTool();
          settingsId = newSettingData.id; // Get settingsId from new tool
          toast.success("Tool created successfully");
          runAutomaticConflictDetection(supabase);
        } else {
          toast.success("Tool settings updated successfully");
        }
        
        // Save requirements for both create and update
        if (settingsId) {
          const { error: deleteError } = await supabase
            .from('brain_setting_requirements')
            .delete()
            .eq('settings_id', settingsId);

          if (deleteError) throw deleteError;

          if (requirements.length > 0) {
            const requirementsToInsert = requirements.map(req => ({
              settings_id: settingsId,
              topic_id: req.topic_id,
              required_answer: req.required_answer,
            }));
            const { error: insertError } = await supabase
              .from('brain_setting_requirements')
              .insert(requirementsToInsert);
            if (insertError) throw insertError;
          }
        }
        
        // Common cleanup for both create and update
        initializedRef.current = false;
        setHasUnsavedChanges(false);
        setHasLocationChanges(false);
        
        // Return structured result for proper handling
        if (newSettingData) {
          onClose({ success: true, settingsId: newSettingData.id, isCreate: true, data: newSettingData });
        } else {
          onClose({ success: true, isUpdate: true });
        }
      }
      
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error(`Failed to save tool: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deletion
  const handleDelete = async () => {
    try {
      // Create a delete request by adding a _delete flag
      const deleteAction = {
        ...action,
        _delete: true
      };
      
      // Send to parent component for processing
      await onSave(deleteAction);
      
      // Reset state and close modals
    setIsDeleteModalOpen(false);
      initializedRef.current = false;
      onClose();
      
      // Show success message
      toast.success("Tool deleted successfully");
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error("Failed to delete tool");
    }
  };

  // Handle modal close with unsaved changes check
  const handleCloseAttempt = () => {
    if (isInline) {
      onClose(); // In inline mode, just call close handler
      return;
    }
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleCloseConfirmed();
    }
  };

  // Confirmed close handling
  const handleCloseConfirmed = () => {
    initializedRef.current = false;
    setIsUnsavedChangesModalOpen(false);
    onClose();
  };

  // Handle location selection
  const handleLocationSelection = (locationId) => {
    setHasUnsavedChanges(true);
    
    // If "All Locations" is selected
    if (locationId === 'all') {
      setFormData(prev => ({
        ...prev,
        all_locations: !prev.all_locations,
        // If turning on all_locations, clear individual selections
        selected_locations: !prev.all_locations ? [] : prev.selected_locations
      }));
      return;
    }
    
    // For individual location selection
    setFormData(prev => {
      // Check if this location is already selected
      const isSelected = prev.selected_locations.includes(locationId);
      
      // If selecting a new location, turn off all_locations
      const newAllLocations = isSelected ? prev.all_locations : false;
      
      // Update selected locations array
      const newSelectedLocations = isSelected
        ? prev.selected_locations.filter(id => id !== locationId)
        : [...prev.selected_locations, locationId];
        
      return {
        ...prev,
        all_locations: newAllLocations,
        selected_locations: newSelectedLocations
      };
    });
    
    // Clear validation error for locations
    if (validationErrors.selected_locations) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.selected_locations;
        return newErrors;
      });
    }
  };

  // Add function to handle tool type selection from modal
  const handleToolTypeSelect = (toolType) => {
    console.log('handleToolTypeSelect called with:', toolType);
    setFormData(prev => ({ ...prev, type: toolType }));
    setHasUnsavedChanges(true);
    setIsToolTypeModalOpen(false);
  };

  // Add function to fetch location groupings
  const fetchLocationGroupings = async () => {
    try {
      // If we have a settings_id, we should use that directly
      if (action && action.id) {
        // Fetch groupings
        const { data, error } = await supabase
          .from('brain_groupings')
          .select('*')
          .eq('settings_id', action.id)
          .order('name');
          
        if (error) throw error;
        
        // Process groupings and their assigned locations
        const processedGroupings = data || [];
        const locationMap = {};
        
        if (processedGroupings.length > 0) {
          // Fetch location assignments
          const groupIds = processedGroupings.map(g => g.id);
          const { data: locData, error: locError } = await supabase
            .from('brain_grouping_locations')
            .select('grouping_id, location_id')
            .in('grouping_id', groupIds);
            
          if (locError) throw locError;
          
          // Map locations to their groupings
          (locData || []).forEach(row => {
            if (!locationMap[row.grouping_id]) {
              locationMap[row.grouping_id] = [];
            }
            locationMap[row.grouping_id].push(row.location_id);
          });
        }
        
        setLocationGroupings(processedGroupings);
        setLocationAssignments(locationMap);
        return;
      }
      
      // Fallback to previous logic if no settings_id is available
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // First, get brain categories for this account
      const { data: categories, error: categoriesError } = await supabase
        .from('brain_categories')
        .select('id')
        .eq('account_id', user.id);
        
      if (categoriesError) throw categoriesError;
      if (!categories?.length) return; // No categories found
      
      // Get category IDs
      const categoryIds = categories.map(cat => cat.id);
      
      // Now get brain_settings using the category IDs
      const { data: brainSettings, error: settingsError } = await supabase
        .from('brain_settings')
        .select('id')
        .in('category_id', categoryIds)
        .limit(1)
        .single();
        
      if (settingsError) return; // No settings found
      
      // Fetch groupings
      const { data, error } = await supabase
        .from('brain_groupings')
        .select('*')
        .eq('settings_id', brainSettings.id)
        .order('name');
        
      if (error) throw error;
      
      // Process groupings and their assigned locations
      const processedGroupings = data || [];
      const locationMap = {};
      
      processedGroupings.forEach(group => {
        locationMap[group.id] = (group.brain_grouping_locations || [])
          .map(loc => loc.location_id);
      });
      
      setLocationGroupings(processedGroupings);
      setLocationAssignments(locationMap);
      
    } catch (error) {
      console.error('Error fetching location groupings:', error);
    }
  };

  // Add function to handle changes from LocationVariantsSidePanel
  const handleLocationVariantsChange = (data) => {
    setLocationGroupings(data.groupings);
    setLocationAssignments(data.assignedLocations);
    
    // Set active grouping if one is selected in the panel
    if (data.selectedGrouping) {
      setActiveLocationGrouping(data.selectedGrouping);
      
      // Initialize form data for this grouping if it doesn't exist
      if (data.selectedGrouping && !formDataByGrouping[data.selectedGrouping]) {
        setFormDataByGrouping(prev => ({
          ...prev,
          [data.selectedGrouping]: { ...formData }
        }));
      }
    }
    
    setHasLocationChanges(true);
    setHasUnsavedChanges(true);
  };

  // Add function to save location groupings
  const saveLocationGroupings = async () => {
    try {
      let settingsId;
      
      // Use id directly from action if available (this is the settings_id)
      if (action && action.id) {
        settingsId = action.id;
      } else {
        // Fallback to finding the settings ID
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Get brain_settings id
        const { data: categories } = await supabase
          .from('brain_categories')
          .select('id')
          .eq('account_id', user.id);
        
        if (!categories?.length) return;
        
        const categoryIds = categories.map(cat => cat.id);
        
        const { data: brainSettings } = await supabase
          .from('brain_settings')
          .select('id')
          .in('category_id', categoryIds)
          .limit(1)
          .single();
          
        if (!brainSettings) return;
        
        settingsId = brainSettings.id;
      }
      
      if (!settingsId) {
        console.error('No settings ID found - cannot save location groupings');
        return;
      }
      
      // Process each grouping
      for (const group of locationGroupings) {
        if (group.isNew) {
          // New grouping - create it
          const { data: newGroup, error } = await supabase
            .from('brain_groupings')
            .insert({
              settings_id: settingsId,
              name: group.name
            })
            .select()
            .single();
            
          if (error) throw error;
          
          // Update assignments with the real ID
          const locations = locationAssignments[group.id] || [];
          
          // Insert location assignments
          if (locations.length > 0) {
            const locationRecords = locations.map(locId => ({
              grouping_id: newGroup.id,
              location_id: locId
            }));
            
            const { error: locError } = await supabase
              .from('brain_grouping_locations')
              .insert(locationRecords);
              
            if (locError) throw locError;
          }
        } else {
          // Existing grouping - update locations
          // First, remove all existing assignments
          const { error: deleteError } = await supabase
            .from('brain_grouping_locations')
            .delete()
            .eq('grouping_id', group.id);
            
          if (deleteError) throw deleteError;
          
          // Then insert the current assignments
          const locations = locationAssignments[group.id] || [];
          
          if (locations.length > 0) {
            const locationRecords = locations.map(locId => ({
              grouping_id: group.id,
              location_id: locId
            }));
            
            const { error: insertError } = await supabase
              .from('brain_grouping_locations')
              .insert(locationRecords);
              
            if (insertError) throw insertError;
          }
        }
      }
      
      // Handle deleted groupings
      const existingGroupIds = locationGroupings.filter(g => !g.isNew).map(g => g.id);
      
      // Get all groupings for this settings_id
      const { data: currentGroups } = await supabase
        .from('brain_groupings')
        .select('id')
        .eq('settings_id', settingsId);
        
      if (currentGroups) {
        // Find IDs that exist in the database but not in our current state
        const deletedIds = currentGroups
          .map(g => g.id)
          .filter(id => !existingGroupIds.includes(id));
        
        // Delete them if any exist
        if (deletedIds.length > 0) {
          const { error: deleteError } = await supabase
            .from('brain_groupings')
            .delete()
            .in('id', deletedIds);
            
          if (deleteError) throw deleteError;
        }
      }
      
    } catch (error) {
      console.error('Error saving location groupings:', error);
    }
  };

  // Add useEffect for location groupings
  useEffect(() => {
    if (isOpen && formData.showLocationVariants) {
      // For new actions, we don't need to fetch from the database
      // as we'll create an initial default grouping client-side
      if (!isNew) {
      fetchLocationGroupings();
      } else {
        // For new actions, initialize with a default grouping 
        // This is handled by the LocationVariantsSidePanel now
        setLocationGroupings([]);
        setLocationAssignments({});
      }
    }
  }, [isOpen, formData.showLocationVariants, isNew]);

  // Handle form changes when location variants are enabled
  const handleFormChangeWithVariants = (e, groupingId = activeLocationGrouping) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Sanitize the value to prevent circular references
    const sanitizedValue = typeof fieldValue === 'object' ? sanitizeFormData({ temp: fieldValue }).temp : fieldValue;

    // 1) Update variant-specific stash
    setFormDataByGrouping(prev => ({
      ...prev,
      [groupingId]: {
        ...(prev[groupingId] || {}),
        [name]: sanitizedValue
      }
    }));

    // 2) Keep main formData in sync so inputs reflect keystrokes immediately
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // 3) Clear validation error for this field if any
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setHasUnsavedChanges(true);
  };

  // Add a handler for direct state updates with variants
  const handleVariantChangeDirectly = (name, value, groupingId = activeLocationGrouping) => {
     // Sanitize the value to prevent circular references
     const sanitizedValue = typeof value === 'object' ? sanitizeFormData({ temp: value }).temp : value;
     
     // 1) Update variant-specific stash
        setFormDataByGrouping(prev => ({
          ...prev,
      [groupingId]: {
        ...(prev[groupingId] || {}),
        [name]: sanitizedValue
      }
        }));

    // 2) Keep main formData in sync
        setFormData(prev => ({
          ...prev,
      [name]: sanitizedValue
    }));

    // 3) Clear validation error for this field if any
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    setHasUnsavedChanges(true);
  };

  // Add useEffect to sync form data with the selected grouping
  useEffect(() => {
    if (formData.showLocationVariants && activeLocationGrouping) {
      // Get the specific fields for the current grouping, or default to empty/initial values
      const groupFields = formDataByGrouping[activeLocationGrouping] || {}; 
      const updatedFormData = { ...formData };

      if (formData.type === 'sms') {
        updatedFormData.sms_message = groupFields.sms_message ?? '';
        updatedFormData.sms_use_contact_phone = groupFields.sms_use_contact_phone ?? false;
        updatedFormData.sms_numbers = groupFields.sms_numbers ?? [];
      } else if (formData.type === 'email') {
        updatedFormData.email_subject = groupFields.email_subject ?? '';
        updatedFormData.email_message = groupFields.email_message ?? '';
        updatedFormData.email_use_contact_email = groupFields.email_use_contact_email ?? false;
        updatedFormData.email_addresses = groupFields.email_addresses ?? [];
      } else if (formData.type === 'transfer') {
        updatedFormData.transfer_number = groupFields.transfer_number ?? '';
        updatedFormData.transfer_name = groupFields.transfer_name ?? '';
        updatedFormData.transfer_start_time = groupFields.transfer_start_time ?? '09:00';
        updatedFormData.transfer_end_time = groupFields.transfer_end_time ?? '17:00';
        // Ensure transfer_days defaults correctly if not present in groupFields
        updatedFormData.transfer_days = groupFields.transfer_days ?? [0,1,2,3,4,5,6];
        // updatedFormData.play_message = groupFields.play_message ?? false; // Removed
      } else if (formData.type === 'api') {
        updatedFormData.api_endpoint = groupFields.api_endpoint ?? '';
        updatedFormData.api_method = groupFields.api_method ?? 'POST';
        updatedFormData.api_body = groupFields.api_body ?? '';
        updatedFormData.api_headers = groupFields.api_headers ?? {};
        updatedFormData.api_query_params = groupFields.api_query_params ?? {};
        updatedFormData.auth_type = groupFields.auth_type ?? 'none';
        updatedFormData.auth_config = groupFields.auth_config ?? {};
      }
      else if (formData.type === 'note') { // Sync for 'note' type
        updatedFormData.note_fields_config = groupFields.note_fields_config ?? [];
      }

      // Only update state if there are actual changes to avoid infinite loops
      let hasChanges = false;
      
      if (formData.type === 'sms') {
        hasChanges = formData.sms_message !== updatedFormData.sms_message ||
                    formData.sms_use_contact_phone !== updatedFormData.sms_use_contact_phone ||
                    JSON.stringify(formData.sms_numbers) !== JSON.stringify(updatedFormData.sms_numbers);
      } else if (formData.type === 'email') {
        hasChanges = formData.email_subject !== updatedFormData.email_subject ||
                    formData.email_message !== updatedFormData.email_message ||
                    formData.email_use_contact_email !== updatedFormData.email_use_contact_email ||
                    JSON.stringify(formData.email_addresses) !== JSON.stringify(updatedFormData.email_addresses);
      } else if (formData.type === 'transfer') {
        hasChanges = formData.transfer_number !== updatedFormData.transfer_number ||
                    formData.transfer_name !== updatedFormData.transfer_name ||
                    formData.transfer_start_time !== updatedFormData.transfer_start_time ||
                    formData.transfer_end_time !== updatedFormData.transfer_end_time ||
                    JSON.stringify(formData.transfer_days) !== JSON.stringify(updatedFormData.transfer_days);
      } else if (formData.type === 'api') {
        hasChanges = formData.api_endpoint !== updatedFormData.api_endpoint ||
                    formData.api_method !== updatedFormData.api_method ||
                    formData.api_body !== updatedFormData.api_body ||
                    JSON.stringify(formData.api_headers) !== JSON.stringify(updatedFormData.api_headers) ||
                    JSON.stringify(formData.api_query_params) !== JSON.stringify(updatedFormData.api_query_params) ||
                    formData.auth_type !== updatedFormData.auth_type ||
                    JSON.stringify(formData.auth_config) !== JSON.stringify(updatedFormData.auth_config);
      } else if (formData.type === 'note') {
        hasChanges = JSON.stringify(formData.note_fields_config) !== JSON.stringify(updatedFormData.note_fields_config);
      }
      
      if (hasChanges) {
      setFormData(updatedFormData);
    }
    }
    // Dependency array fixed: removed formDataByGrouping to prevent infinite re-renders
  }, [activeLocationGrouping, formData.showLocationVariants, formData.type]);

  // Special handler for transfer number formatting
  const handleTransferNumberChange = (e) => {
    const formattedValue = formatPhoneNumberAsUserTypes(e.target.value);
    
    if (formData.showLocationVariants) {
      handleVariantChangeDirectly('transfer_number', formattedValue);
    } else {
      setFormData(prev => ({ ...prev, transfer_number: formattedValue }));
      setHasUnsavedChanges(true);
    }
    
    // Clear validation error
    if (validationErrors.transfer_number) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.transfer_number;
        return newErrors;
      });
    }
  };

  // API Testing functionality
  const handleTestAPI = async () => {
    if (!formData.api_endpoint) {
      toast.error("Please enter an API endpoint first");
      return;
    }
    setIsTestingAPI(true);
    setTestResult(null);
    
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      
      const currentTestInputs = { ...testInputs };
      if (showQuestions && formData.questions) {
        formData.questions.forEach((question, index) => {
          const questionKey = `question_${index + 1}_response`;
          if (!currentTestInputs[questionKey]) {
            currentTestInputs[questionKey] = question.type === 'boolean' ? 'yes' : 
                                           question.type === 'numeric' ? '1' : 
                                           `Sample answer for ${question.question}`;
          }
        });
        setTestInputs(currentTestInputs);
      }

      const response = await fetch(`${API_BASE_URL}/api/external/simple-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_id: accountId,
          api_endpoint: formData.api_endpoint,
          api_method: formData.api_method || 'POST',
          api_headers: formData.api_headers || {},
          api_body: { type: "simple_action_api_call", responses: currentTestInputs },
          auth_type: formData.auth_type || 'none',
          auth_config: formData.auth_config || {}
        })
      });

      const responseData = response.ok ? await response.json() : { error: await response.text() };
      
      setTestResult({
        success: response.ok,
        data: response.ok ? responseData : undefined,
        error: response.ok ? undefined : responseData.error,
        status: response.status,
        timestamp: new Date().toISOString()
      });

      toast.success(response.ok ? "API test successful! 🎉" : "API test failed");
    } catch (error) {
      setTestResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });
      toast.error(`API test failed: ${error.message}`);
    } finally {
      setIsTestingAPI(false);
    }
  };

  // If not open, don't render
  if (!isOpen) return null;

  const renderContent = () => (
      <motion.div 
      className={cn(
        "bg-gray-50 flex flex-col overflow-hidden",
        isInline 
          ? "w-full h-full" 
          : "rounded-2xl shadow-2xl w-[90vw] max-h-[80vh] relative border border-gray-200/50"
      )}
      variants={!isInline ? modalVariants : {}}
      initial={!isInline ? "hidden" : ""}
      animate={!isInline ? "visible" : ""}
      exit={!isInline ? "exit" : ""}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white z-20 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center shadow-sm border border-amber-200/50">
                <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              </div>
              <h2 className="font-medium text-base sm:text-lg text-gray-900">
                {isNew ? 'New Simple Tool' : 'Edit Simple Tool'}
              </h2>
            </div>

            <div className="flex items-center space-x-2">
              {isInline && (
                <div className="flex items-center gap-2">
                  {onOpenActivationConditions && setting && (
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenActivationConditions(setting)}
                        className="text-gray-700 border-gray-200 hover:bg-gray-50"
                     >
                       <Clock className="w-4 h-4 mr-2" />
                       Conditions
                     </Button>
                  )}
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <CircleNotch className="w-4 h-4 animate-spin" />
                        Saving
                      </span>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              )}
              
              {!isInline && (
                <button
                  type="button"
                  onClick={handleCloseAttempt}
                  className="rounded-lg h-8 w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Tabs Navigation */}
          <div className="border-b border-gray-200 bg-white">
            <div className="px-4 sm:px-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("details")}
                  className={cn(
                    "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
                    activeTab === "details"
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  )}
                >
                  Details
                  {Object.keys(validationErrors).some(key => ['name', 'description', 'category_id'].includes(key)) && (
                    <span className="ml-2 inline-block w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>
                
                <button
                  onClick={() => setActiveTab("triggers")}
                  className={cn(
                    "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
                    activeTab === "triggers"
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  )}
                >
                  Triggers
                  {validationErrors.triggers && (
                    <span className="ml-2 inline-block w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>
                
                {!hideActionTab && (
                  <button
                    onClick={() => setActiveTab("message")}
                    className={cn(
                      "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap relative",
                      activeTab === "message"
                        ? "border-amber-500 text-amber-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    )}
                  >
                    {/* Grouping badge */}
                    {formData.showLocationVariants && activeLocationGrouping && locationGroupings.length > 0 && (
                      <span className="absolute -top-1 -right-2 bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">
                        {locationGroupings.find(g => g.id === activeLocationGrouping)?.name || 'Default'}
                      </span>
                    )}

                    {/* Label */}
                    {!formData.type ? 'Action' : 
                     formData.type === 'sms' ? 'Message' : 
                     formData.type === 'email' ? 'Email' : 
                     formData.type === 'transfer' ? 'Transfer' : 
                     formData.type === 'api' ? 'API' :
                     formData.type === 'note' ? 'Note Fields' : 'Action'}

                    {/* Error dot */}
                    {Object.keys(validationErrors).some(key => [
                      'sms_message', 'link_url', 'transfer_number', 'transfer_message', 'api_endpoint', 'api_body', 'note_fields_config', 'note_fields_tab_error'
                    ].includes(key)) && (
                      <span className="ml-2 inline-block w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </button>
                )}
                
                {!isNew && (
                  <button
                    onClick={() => setActiveTab("delete")}
                    className={cn(
                      "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
                      activeTab === "delete"
                        ? "border-red-500 text-red-600"
                        : "border-transparent text-red-500/70 hover:text-red-500 hover:border-red-300"
                    )}
                  >
                    Danger
                  </button>
                )}
              </nav>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className={cn("flex-1 overflow-y-auto bg-gray-50", !isInline && "pb-0")}> 
            {/* Simple Action Tool Modal - positioned relative to tab content */}
            {isInline && (
              <SimpleActionToolModal
                isOpen={isToolTypeModalOpen}
                onClose={() => setIsToolTypeModalOpen(false)}
                onToolSelect={handleToolTypeSelect}
                isInline={isInline}
              />
            )}
            
            {/* Loading Skeleton Overlay */}
            {modalIsLoading && (
              <div className="absolute inset-0 bg-white z-50 p-6">
                <CallActionSettingsSkeleton />
              </div>
            )}
            
            {/* Actual Tab Content (Render only when not loading) */}
            {!modalIsLoading && (
              <div className="p-6 space-y-6"> 
                {/* Details Tab */} 
                {activeTab === "details" && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-6">
                  {/* Name Field */}
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="name" className="text-xs sm:text-sm font-medium flex items-center">
                      Tool Name <span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter a descriptive name"
                      className={cn(
                        "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                        validationErrors.name && "border-red-300 focus:border-red-500 focus:ring-red-500"
                      )}
                    />
                    {validationErrors.name && (
                      <motion.p 
                        className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                      >
                        {validationErrors.name}
                      </motion.p>
                    )}
                  </div>
                  
                  {/* Description Field */}
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="description" className="text-xs sm:text-sm font-medium flex items-center">
                      Description <span className="text-gray-400 ml-1 text-[10px] sm:text-xs font-normal">(Optional)</span>
                    </Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange}
                      placeholder="Add a brief description about this tool"
                      className="min-h-[70px] sm:min-h-[80px] text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors resize-none"
                    />
                  </div>
                  
                  {/* Action Type Selector */}
                  <div className="space-y-2 sm:space-y-3 pt-3 sm:pt-4 border-t border-gray-100">
                    <Label className="text-xs sm:text-sm font-medium">
                      Tool Type <span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    
                    {formData.type ? (
                      <div 
                        className="flex items-center p-3 border rounded-lg border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => {
                          console.log('Tool type change button clicked, opening modal');
                          setIsToolTypeModalOpen(true);
                        }}
                      >
                        <div className="flex-1 flex items-center">
                          {formData.type === 'sms' && (
                            <>
                              <ChatCircle className="h-4 w-4 text-blue-500 mr-2" />
                              <span className="font-medium text-sm text-gray-900">Text Message</span>
                            </>
                          )}
                          {formData.type === 'email' && (
                            <>
                              <Envelope className="h-4 w-4 text-indigo-500 mr-2" />
                              <span className="font-medium text-sm text-gray-900">Email</span>
                            </>
                          )}
                          {formData.type === 'transfer' && (
                            <>
                              <PhoneTransfer className="h-4 w-4 text-green-500 mr-2" />
                              <span className="font-medium text-sm text-gray-900">Transfer Call</span>
                            </>
                          )}
                          {formData.type === 'api' && (
                            <>
                              <WebhooksLogo className="h-4 w-4 text-purple-500 mr-2" />
                              <span className="font-medium text-sm text-gray-900">API</span>
                            </>
                          )}
                          {formData.type === 'dtmf' && (
                            <>
                              <Hash className="h-4 w-4 text-orange-500 mr-2" />
                              <span className="font-medium text-sm text-gray-900">DTMF</span>
                            </>
                          )}
                          {formData.type === 'note' && (
                            <>
                              <Notepad className="h-4 w-4 text-yellow-500 mr-2" />
                              <span className="font-medium text-sm text-gray-900">Take Note</span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-blue-600 font-medium">Change</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          console.log('Tool type button clicked, opening modal');
                          setIsToolTypeModalOpen(true);
                        }}
                        className={cn(
                          "w-full border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center transition-colors bg-gray-50 hover:bg-gray-100",
                          validationErrors.type ? "border-red-300" : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <div className={cn(
                          "h-8 w-8 rounded-full flex items-center justify-center mb-2",
                          validationErrors.type ? "bg-red-100" : "bg-blue-100"
                        )}>
                          <Plus className={cn(
                            "h-4 w-4",
                            validationErrors.type ? "text-red-600" : "text-blue-600"
                          )} />
                        </div>
                        <p className={cn(
                          "text-sm font-medium",
                          validationErrors.type ? "text-red-700" : "text-gray-700"
                        )}>
                          Select Tool Type
                        </p>
                        <p className={cn(
                          "text-xs mt-1",
                          validationErrors.type ? "text-red-500" : "text-gray-500"
                        )}>
                          {validationErrors.type || "Choose the action this tool will perform"}
                        </p>
                      </button>
                    )}
                  </div>
                  
                  {/* Category Dropdown */}
                  <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                    <Label htmlFor="category" className="text-xs sm:text-sm font-medium flex items-center">
                      Category <span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <Select 
                      value={formData.category_id} 
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger
                        id="category"
                        className={cn(
                          "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                          validationErrors.category_id && "border-red-300 focus:border-red-500 focus:ring-red-500"
                        )}
                      >
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent className="z-[15000]">
                        {categories.map(category => (
                          <SelectItem key={category.id} value={category.id} className="text-sm">
                            {category.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="new" className="text-sm text-blue-600">
                          <div className="flex items-center">
                            <Plus className="h-3.5 w-3.5 mr-1.5" />
                            Create new category
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {validationErrors.category_id && (
                      <motion.p 
                        className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                      >
                        {validationErrors.category_id}
                      </motion.p>
                    )}
                  </div>
                      
                      {/* Qualifiers Section */}
                      {accountId && action?.settings_id && (
                        <div className="pt-3 sm:pt-4 border-t border-gray-100">
                           <BrainSettingConditions
                            settingId={action.settings_id}
                            accountId={accountId}
                            initialRequirements={requirements}
                            onRequirementsChange={(newReqs) => {
                              setRequirements(newReqs);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      )}

                      {/* Location Variants Toggle - Only show if multiple locations exist - REMOVED PER USER REQUEST */}
                      {/* {fetchedLocations && fetchedLocations.length > 1 && (
                        <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100">
                          <div className="space-y-0.5 sm:space-y-1">
                            <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 text-gray-500" />
                              Enable Location Variants
                            </h4>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Create different versions of this tool for specific locations
                            </p>
                </div>
                          <Switch
                            checked={formData.showLocationVariants}
                            onCheckedChange={(checked) => {
                              setFormData(prev => ({ ...prev, showLocationVariants: checked }));
                              setHasUnsavedChanges(true);
                            }}
                            className="data-[state=checked]:bg-blue-500"
                          />
                        </div>
                      )} */}
                    </div>
                  )}
                  
                  {/* Triggers Tab */} 
                  {activeTab === "triggers" && (
                    <div className="space-y-4 sm:space-y-6 py-3 sm:py-4">
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center mb-1 sm:mb-2">
                          <ChatCircle size={isMobile ? 14 : 16} className="text-gray-600 mr-1.5 sm:mr-2" />
                          <span className="text-xs text-gray-600 font-medium">Trigger Keywords</span>
                          <span className="text-red-500 ml-0.5">*</span>
                        </div>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                          Add words or phrases that will trigger this tool during calls
                        </p>
                      </div>
                      
                      {formData.triggers.length > 0 && (
                        <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                          {formData.triggers.length} {formData.triggers.length === 1 ? 'keyword' : 'keywords'}
                        </div>
                      )}
                    </div>
                    
                    {/* Display existing keywords */}
                    <div className="min-h-[120px] sm:min-h-[150px] bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 relative">
                      {formData.triggers.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-6 sm:py-8">
                          <MagicWand className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mb-1.5 sm:mb-2" />
                          <p className="text-xs sm:text-sm text-gray-400">No keywords added yet</p>
                          <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1">Add some keywords to trigger this tool</p>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          <AnimatePresence>
                            {formData.triggers.map((trigger, index) => (
                              <motion.div
                                key={`${trigger}-${index}`}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
                                transition={{ duration: 0.2 }}
                                className="bg-white border border-gray-200 text-gray-800 text-[10px] sm:text-xs px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-md flex items-center shadow-sm"
                              >
                                <span>{trigger}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTrigger(trigger)}
                                  className="ml-1 sm:ml-1.5 text-gray-400 hover:text-gray-600 focus:outline-none p-0.5"
                                >
                                  <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                </button>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                    
                    {validationErrors.triggers && (
                      <motion.p 
                        className="text-[11px] sm:text-xs text-red-500 flex items-center"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                      >
                        {validationErrors.triggers}
                      </motion.p>
                    )}
                    
                    {/* Add keyword form */}
                    <div className="mt-3 sm:mt-4">
                      <form onSubmit={handleAddTrigger} className="flex space-x-2">
                        <Input
                          value={currentTrigger}
                          onChange={(e) => setCurrentTrigger(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddTrigger(e);
                            }
                          }}
                          placeholder="Type a keyword and press Enter"
                          className="flex-1 h-9 sm:h-10 text-sm border-gray-200"
                        />
                        <Button 
                          type="submit" 
                          disabled={!currentTrigger.trim()}
                          className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3"
                          size={isMobile ? "sm" : "default"}
                        >
                          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                          Add
                        </Button>
                      </form>
                    </div>
                    
                    {/* Tips */}
                    <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3 sm:p-4">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
                        Tips for effective keywords
                      </h4>
                      <ul className="mt-1.5 sm:mt-2 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-gray-600">
                        <li className="flex items-start">
                          <div className="rounded-full h-1 w-1 bg-blue-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
                          <span>Use natural language phrases customers are likely to say</span>
                        </li>
                        <li className="flex items-start">
                          <div className="rounded-full h-1 w-1 bg-blue-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
                          <span>Add variations of common requests (e.g., "hours", "when are you open")</span>
                        </li>
                        <li className="flex items-start">
                          <div className="rounded-full h-1 w-1 bg-blue-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
                          <span>Keep keywords specific to avoid false triggers</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                  )}
                  
                  {/* Message Tab */} 
                  {!hideActionTab && activeTab === "message" && (
                    <div className="space-y-4 sm:space-y-6 py-3 sm:py-4 relative">
                    {/* Placeholder when no type is selected */}
                    {!formData.type && (
                      <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                          <Plus className="h-5 w-5 text-gray-400" />
                        </div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">No Tool Type Selected</h3>
                        <p className="text-xs text-gray-500 text-center max-w-sm mb-4">
                          Please select a tool type in the details tab to configure actions
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                              setActiveTab("details");
                            setIsToolTypeModalOpen(true);
                          }}
                          className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 py-2 px-3 rounded-md font-medium transition-colors"
                        >
                          Select Tool Type
                        </button>
                      </div>
                    )}
                    
                    {/* SMS Message Content */}
                    {formData.type === 'sms' && (
                      <>
                        {/* Message Preview - For SMS */}
                        <div className="space-y-1.5 sm:space-y-2">
                          <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2 flex items-center justify-between">
                            <span>Message Preview</span>
                            <button
                              type="button"
                              onClick={() => setExpandedActionType('sms')}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
                            >
                              <span>Expand</span>
                              <ArrowsOutSimple className="h-3.5 w-3.5 ml-1" />
                            </button>
                          </h3>
                          
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4">
                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                              <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap">
                                {formData.sms_message || 'Your message will appear here'}
                              </p>
                              
                              {formData.link_url && (
                                <div className="mt-1.5 sm:mt-2 flex items-center">
                                  <Link className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-blue-500 mr-1" />
                                  <a 
                                    href="#" 
                                    onClick={(e) => e.preventDefault()}
                                    className="text-[10px] sm:text-xs text-blue-500 hover:underline truncate max-w-full"
                                  >
                                    {formData.link_url}
                                  </a>
                                </div>
                              )}
                              
                              <div className="absolute -top-2 -left-2 bg-indigo-100 text-indigo-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                                Preview
                              </div>
                            </div>
                          </div>
                        </div>
                        

                        
                        {/* Questions Toggle for SMS */}
                        <div className="pt-3 sm:pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5 sm:space-y-1">
                              <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                                <Question className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 text-blue-500" />
                                Ask Questions First
                              </h4>
                              <p className="text-[10px] sm:text-xs text-gray-500">
                                Ask questions before sending message to personalize content
                              </p>
                            </div>
                            <Switch
                              checked={showQuestions}
                              onCheckedChange={(checked) => {
                                setShowQuestions(checked);
                                setHasUnsavedChanges(true);
                                if (!checked) {
                                  // Clear questions when toggled off
                                  const sanitizedQuestions = sanitizeFormData({ temp: [] }).temp;
                                  setFormData(prev => ({ ...prev, questions: sanitizedQuestions }));
                                }
                              }}
                              className="data-[state=checked]:bg-blue-500"
                            />
                          </div>
                        </div>

                        {/* Questions Tab for SMS */}
                        {showQuestions && (
                          <div className="space-y-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                              <h3 className="text-xs sm:text-sm font-medium text-gray-700">Questions</h3>
                              <span className="text-[10px] sm:text-xs text-gray-500">
                                {(formData.questions || []).length} question{(formData.questions || []).length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            
                            {/* Add Question Form */}
                            <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                              <div className="flex gap-2">
                                <Input
                                  value={currentQuestion}
                                  onChange={(e) => setCurrentQuestion(e.target.value)}
                                  placeholder="Enter a question to ask"
                                  className="flex-1 h-8 text-sm"
                                />
                                <Select value={currentQuestionType} onValueChange={setCurrentQuestionType}>
                                  <SelectTrigger className="w-24 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="z-[10030]">
                                    <SelectItem value="string">Text</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 px-3 text-xs"
                                  onClick={() => {
                                    if (!currentQuestion.trim()) return;
                                    const newQuestion = {
                                      id: Date.now().toString(),
                                      question: currentQuestion.trim(),
                                      type: currentQuestionType,
                                      order: (formData.questions || []).length
                                    };
                                    const sanitizedQuestions = sanitizeFormData({ temp: [...(formData.questions || []), newQuestion] }).temp;
                                    setFormData(prev => ({
                                      ...prev,
                                      questions: sanitizedQuestions
                                    }));
                                    setCurrentQuestion('');
                                    setHasUnsavedChanges(true);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                            </div>

                            {/* Questions List */}
                            {(formData.questions || []).length > 0 && (
                              <div className="space-y-2">
                                {(formData.questions || []).map((question, index) => (
                                  <div key={question.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-900">Q{index + 1}:</span>
                                        <span className="text-xs text-gray-700">{question.question}</span>
                                        <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                          {question.type}
                                        </span>
                                      </div>
                                      <div className="text-[10px] text-blue-600 mt-1">
                                        Variable: question_{index + 1}_response
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const filteredQuestions = formData.questions.filter(q => q.id !== question.id);
                                        const sanitizedQuestions = sanitizeFormData({ temp: filteredQuestions }).temp;
                                        setFormData(prev => ({
                                          ...prev,
                                          questions: sanitizedQuestions
                                        }));
                                        setHasUnsavedChanges(true);
                                      }}
                                      className="text-gray-400 hover:text-red-500 p-1"
                                    >
                                      <Trash className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Message Content - Now Third */}
                        <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                          <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                            <ChatCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
                            Text Message
                            <span className="text-red-500 ml-0.5">*</span>
                          </h3>
                          <p className="text-[10px] sm:text-xs text-gray-500">
                            This message will be sent when the tool is triggered{showQuestions ? '. Use @question_1_response, @question_2_response, etc. to reference answers.' : ''}
                          </p>
                          
                          <MentionInput
                            id="sms_message"
                            name="sms_message"
                            value={formData.sms_message}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (formData.showLocationVariants) {
                                handleVariantChangeDirectly('sms_message', value);
                              } else {
                                setFormData(prev => ({ ...prev, sms_message: value }));
                                setHasUnsavedChanges(true);
                              }
                            }}
                            placeholder="Enter the message to send when this tool is triggered"
                            className={cn(
                              "min-h-[100px] sm:min-h-[120px] text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                              validationErrors.sms_message && "border-red-300 focus:border-red-500 focus:ring-red-500"
                            )}
                            questions={questionsForMention}
                            multiline={true}
                          />
                          
                          <div className="flex justify-between items-center">
                            <div className={cn(
                              "text-[10px] sm:text-xs", 
                              formData.sms_message.length > 160 ? "text-red-500" : "text-gray-500"
                            )}>
                              {formData.sms_message.length}/160 characters
                            </div>
                            
                            {validationErrors.sms_message && (
                              <motion.p 
                                className="text-[11px] sm:text-xs text-red-500 flex items-center"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                {validationErrors.sms_message}
                              </motion.p>
                            )}
                          </div>
                        </div>
                        
                        {/* SMS Recipient Option */}
                        <div className="pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5 sm:space-y-1">
                              <h4 className="text-xs sm:text-sm font-medium text-gray-700">Send to specific numbers</h4>
                              <p className="text-[10px] sm:text-xs text-gray-500">
                                {formData.sms_use_contact_phone 
                                  ? "Uses pre-defined phone numbers" 
                                  : "Uses caller's phone number automatically"}
                              </p>
                            </div>
                            <Switch
                              id="sms_use_contact_phone"
                              name="sms_use_contact_phone"
                              checked={formData.sms_use_contact_phone}
                              onCheckedChange={(checked) => { // Updated handler
                                if (formData.showLocationVariants) {
                                  handleVariantChangeDirectly('sms_use_contact_phone', checked);
                                } else {
                                setFormData(prev => ({ ...prev, sms_use_contact_phone: checked }));
                                setHasUnsavedChanges(true);
                                }
                              }}
                              className="data-[state=checked]:bg-blue-500"
                            />
                          </div>
                        </div>
                        
                        {/* Only show recipients field if use_contact_phone is true */}
                        {formData.sms_use_contact_phone && (
                          <div className="space-y-1.5 sm:space-y-2 pt-3">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-blue-500" />
                              Recipients
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Add phone numbers that will receive this message
                            </p>
                            
                            <PhoneNumberInput 
                              phoneNumbers={formData.sms_numbers || []} // Ensure array
                              onChange={(numbers) => { // Updated onChange
                                if (formData.showLocationVariants) {
                                  handleVariantChangeDirectly('sms_numbers', numbers);
                                } else {
                                  setFormData(prev => ({ ...prev, sms_numbers: numbers }));
                                setHasUnsavedChanges(true);
                                }
                                // Clear validation (common logic)
                                if (validationErrors.sms_numbers) {
                                  setValidationErrors(prev => {
                                    const newErrors = {...prev};
                                    delete newErrors.sms_numbers;
                                    return newErrors;
                                  });
                                }
                              }}
                              error={validationErrors.sms_numbers}
                            />
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Email Content */}
                    {formData.type === 'email' && (
                      <div className="space-y-5">
                        {/* Email Preview */}
                        <div className="space-y-1.5 sm:space-y-2">
                          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700">Email Preview</h3>
                            <button
                              type="button"
                              onClick={() => setExpandedActionType('email')}
                              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                            >
                              <span>Expand</span>
                              <ArrowsOutSimple className="h-3.5 w-3.5 ml-1" />
                            </button>
                          </div>
                          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 sm:p-5">
                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                              <div className="mb-4 border-b border-gray-100 pb-2">
                                <div className="flex items-center text-xs sm:text-sm text-gray-700 mb-1">
                                  <span className="font-medium mr-1">From:</span>
                                  <span>Your Business</span>
                                </div>
                                <div className="flex items-center text-xs sm:text-sm text-gray-700 mb-1">
                                  <span className="font-medium mr-1">To:</span>
                                  <span>{formData.email_addresses.length > 0 ? formData.email_addresses.join(', ') : 'Recipients will appear here'}</span>
                                </div>
                                <div className="flex items-center text-xs sm:text-sm text-gray-900 font-medium">
                                  <span className="mr-1">Subject:</span>
                                  <span>{formData.email_subject || 'Your email subject will appear here'}</span>
                                </div>
                              </div>
                              <p className="text-xs sm:text-sm text-gray-800 whitespace-pre-wrap">
                                {formData.email_message || 'Your email message will appear here'}
                              </p>
                              
                              <div className="absolute -top-2 -left-2 bg-indigo-100 text-indigo-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                                Preview
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Questions Toggle for Email */}
                        <div className="pt-3 sm:pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5 sm:space-y-1">
                              <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                                <Question className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 text-indigo-500" />
                                Ask Questions First
                              </h4>
                              <p className="text-[10px] sm:text-xs text-gray-500">
                                Ask questions before sending email to personalize content
                              </p>
                            </div>
                            <Switch
                              checked={showQuestions}
                              onCheckedChange={(checked) => {
                                setShowQuestions(checked);
                                setHasUnsavedChanges(true);
                                if (!checked) {
                                  // Clear questions when toggled off
                                  const sanitizedQuestions = sanitizeFormData({ temp: [] }).temp;
                                  setFormData(prev => ({ ...prev, questions: sanitizedQuestions }));
                                }
                              }}
                              className="data-[state=checked]:bg-indigo-500"
                            />
                          </div>
                        </div>

                        {/* Questions Tab for Email */}
                        {showQuestions && (
                          <div className="space-y-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                              <h3 className="text-xs sm:text-sm font-medium text-gray-700">Questions</h3>
                              <span className="text-[10px] sm:text-xs text-gray-500">
                                {(formData.questions || []).length} question{(formData.questions || []).length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            
                            {/* Add Question Form */}
                            <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                              <div className="flex gap-2">
                                <Input
                                  value={currentQuestion}
                                  onChange={(e) => setCurrentQuestion(e.target.value)}
                                  placeholder="Enter a question to ask"
                                  className="flex-1 h-8 text-sm"
                                />
                                <Select value={currentQuestionType} onValueChange={setCurrentQuestionType}>
                                  <SelectTrigger className="w-24 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="z-[10030]">
                                    <SelectItem value="string">Text</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 px-3 text-xs"
                                  onClick={() => {
                                    if (!currentQuestion.trim()) return;
                                    const newQuestion = {
                                      id: Date.now().toString(),
                                      question: currentQuestion.trim(),
                                      type: currentQuestionType,
                                      order: (formData.questions || []).length
                                    };
                                    const sanitizedQuestions = sanitizeFormData({ temp: [...(formData.questions || []), newQuestion] }).temp;
                                    setFormData(prev => ({
                                      ...prev,
                                      questions: sanitizedQuestions
                                    }));
                                    setCurrentQuestion('');
                                    setHasUnsavedChanges(true);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                            </div>

                            {/* Questions List */}
                            {(formData.questions || []).length > 0 && (
                              <div className="space-y-2">
                                {(formData.questions || []).map((question, index) => (
                                  <div key={question.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-gray-900">Q{index + 1}:</span>
                                        <span className="text-xs text-gray-700">{question.question}</span>
                                        <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                          {question.type}
                                        </span>
                                      </div>
                                      <div className="text-[10px] text-blue-600 mt-1">
                                        Variable: question_{index + 1}_response
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const filteredQuestions = formData.questions.filter(q => q.id !== question.id);
                                        const sanitizedQuestions = sanitizeFormData({ temp: filteredQuestions }).temp;
                                        setFormData(prev => ({
                                          ...prev,
                                          questions: sanitizedQuestions
                                        }));
                                        setHasUnsavedChanges(true);
                                      }}
                                      className="text-gray-400 hover:text-red-500 p-1"
                                    >
                                      <Trash className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Email Subject */}
                        <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                          <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                            <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                            Email Subject
                            <span className="text-red-500 ml-0.5">*</span>
                          </h3>
                          <p className="text-[10px] sm:text-xs text-gray-500">
                            Enter the subject line for your email{showQuestions ? '. Use @question_1_response, @question_2_response, etc. to reference answers.' : ''}
                          </p>
                          
                          <MentionInput
                            id="email_subject"
                            name="email_subject"
                            value={formData.email_subject}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (formData.showLocationVariants) {
                                handleVariantChangeDirectly('email_subject', value);
                              } else {
                                const sanitizedValue = typeof value === 'object' ? sanitizeFormData({ temp: value }).temp : value;
                                setFormData(prev => ({ ...prev, email_subject: sanitizedValue }));
                                setHasUnsavedChanges(true);
                              }
                            }}
                            placeholder="Enter email subject line"
                            className={cn(
                              "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                              validationErrors.email_subject && "border-red-300 focus:border-red-500 focus:ring-red-500"
                            )}
                            questions={questionsForMention}
                          />
                          
                          {validationErrors.email_subject && (
                            <motion.p 
                              className="text-[11px] sm:text-xs text-red-500 flex items-center"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                            >
                              {validationErrors.email_subject}
                            </motion.p>
                          )}
                        </div>
                        
                        {/* Email Message */}
                        <div className="space-y-1.5 sm:space-y-2">
                          <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                            <ChatCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                            Email Message
                            <span className="text-red-500 ml-0.5">*</span>
                          </h3>
                          <p className="text-[10px] sm:text-xs text-gray-500">
                            Enter the email message body{showQuestions ? '. Use @question_1_response, @question_2_response, etc. to reference answers.' : ''}
                          </p>
                          
                          <MentionInput
                            id="email_message"
                            name="email_message"
                            value={formData.email_message}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (formData.showLocationVariants) {
                                handleVariantChangeDirectly('email_message', value);
                              } else {
                                const sanitizedValue = typeof value === 'object' ? sanitizeFormData({ temp: value }).temp : value;
                                setFormData(prev => ({ ...prev, email_message: sanitizedValue }));
                                setHasUnsavedChanges(true);
                              }
                            }}
                            placeholder="Enter the email message to send when this tool is triggered"
                            className={cn(
                              "min-h-[120px] sm:min-h-[140px] text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                              validationErrors.email_message && "border-red-300 focus:border-red-500 focus:ring-red-500"
                            )}
                            questions={questionsForMention}
                            multiline={true}
                          />
                          
                          {validationErrors.email_message && (
                            <motion.p 
                              className="text-[11px] sm:text-xs text-red-500 flex items-center"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                            >
                              {validationErrors.email_message}
                            </motion.p>
                          )}
                        </div>
                        
                        {/* Email Recipient Option */}
                        <div className="pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5 sm:space-y-1">
                              <h4 className="text-xs sm:text-sm font-medium text-gray-700">Send to specific addresses</h4>
                              <p className="text-[10px] sm:text-xs text-gray-500">
                                {formData.email_use_contact_email 
                                  ? "Uses pre-defined email addresses" 
                                  : "Asks caller for their email address"}
                              </p>
                            </div>
                            <Switch
                              id="email_use_contact_email"
                              name="email_use_contact_email"
                              checked={formData.email_use_contact_email}
                              onCheckedChange={(checked) => { // Updated handler
                                if (formData.showLocationVariants) {
                                  handleVariantChangeDirectly('email_use_contact_email', checked);
                                } else {
                                  const sanitizedChecked = typeof checked === 'object' ? sanitizeFormData({ temp: checked }).temp : checked;
                                  setFormData(prev => ({ ...prev, email_use_contact_email: sanitizedChecked }));
                                  setHasUnsavedChanges(true);
                                }
                              }}
                              className="data-[state=checked]:bg-indigo-500"
                            />
                          </div>
                        </div>
                        
                        {/* Email Recipients - only show if use_contact_email is true */}
                        {formData.email_use_contact_email && (
                          <div className="space-y-1.5 sm:space-y-2 pt-3">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-indigo-500" />
                              Recipients
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Add email addresses that will receive this email
                            </p>
                            
                            <EmailAddressInput 
                              addresses={formData.email_addresses || []} // Ensure array
                              onChange={(addresses) => { // Updated onChange
                                if (formData.showLocationVariants) {
                                  handleVariantChangeDirectly('email_addresses', addresses);
                                } else {
                                  const sanitizedAddresses = typeof addresses === 'object' ? sanitizeFormData({ temp: addresses }).temp : addresses;
                                  setFormData(prev => ({ ...prev, email_addresses: sanitizedAddresses }));
                                  setHasUnsavedChanges(true);
                                }
                                 // Clear validation (common logic)
                                if (validationErrors.email_addresses) {
                                  setValidationErrors(prev => {
                                    const newErrors = {...prev};
                                    delete newErrors.email_addresses;
                                    return newErrors;
                                  });
                                }
                              }}
                              error={validationErrors.email_addresses}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Transfer Call Content */}
                    {formData.type === 'transfer' && (
                      <div className="space-y-5">
                        {/* Transfer Preview */}
                        <div className="space-y-1.5 sm:space-y-2">
                          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700">Transfer Preview</h3>
                            <button
                              type="button"
                              onClick={() => setExpandedActionType('transfer')}
                              className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center"
                            >
                              <span>Expand</span>
                              <Maximize2 className="h-3.5 w-3.5 ml-1" />
                            </button>
                          </div>
                          <div className="bg-green-50 border border-green-100 rounded-lg p-4 sm:p-5">
                            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                              <div className="text-xs sm:text-sm font-medium text-gray-900 mb-1">
                                {formData.transfer_name || 'Transfer Name'}
                              </div>
                              <div className="flex items-center text-xs sm:text-sm text-gray-700 mb-3">
                                <PhoneForwarded className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-500 mr-1.5" />
                                <span>{formData.transfer_number || 'Phone number will appear here'}</span>
                              </div>
                              
                              <div className="bg-gray-50 rounded-lg p-2 text-[10px] sm:text-xs">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="font-medium text-gray-700">Operating Hours:</span>
                                  <span className="text-gray-600">{formData.transfer_start_time} - {formData.transfer_end_time}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-700">Days Available:</span>
                                  <span className="text-gray-600">
                                    {formData.transfer_days.length === 7 
                                      ? 'Every day' 
                                      : formData.transfer_days.map(day => 
                                          ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                                        ).join(', ')}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="absolute -top-2 -left-2 bg-green-100 text-green-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                                Preview
                              </div>
                            </div>
                          </div>

                          {/* Transfer Name */}
                          <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <Building className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                              Transfer Name
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Name for this transfer (e.g., "Support Team", "Sales Department")
                            </p>
                            
                            <Input
                              id="transfer_name"
                              name="transfer_name"
                              value={formData.transfer_name}
                              onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                              placeholder="Enter name for transfer"
                              className={cn(
                                "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                                validationErrors.transfer_name && "border-red-300 focus:border-red-500 focus:ring-red-500"
                              )}
                            />
                            
                            {validationErrors.transfer_name && (
                              <motion.p 
                                className="text-[11px] sm:text-xs text-red-500 flex items-center"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                {validationErrors.transfer_name}
                              </motion.p>
                            )}
                          </div>

                          {/* Transfer Number */}
                          <div className="space-y-1.5 sm:space-y-2">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <PhoneForwarded className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                              Transfer Number
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Enter the phone number to forward calls to when triggered
                            </p>
                            
                            <Input
                              id="transfer_number"
                              name="transfer_number"
                              value={formData.transfer_number}
                              onChange={handleTransferNumberChange}
                              placeholder="(555) 555-5555"
                              className={cn(
                                "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors",
                                validationErrors.transfer_number && "border-red-300 focus:border-red-500 focus:ring-red-500"
                              )}
                            />
                            
                            {validationErrors.transfer_number && (
                              <motion.p 
                                className="text-[11px] sm:text-xs text-red-500 flex items-center"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                {validationErrors.transfer_number}
                              </motion.p>
                            )}
                          </div>

                          {/* Operating Hours */}
                          <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                              Operating Hours
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Set hours when this transfer is active
                            </p>
                            
                            <div className="grid grid-cols-2 gap-2 sm:gap-3">
                              <div>
                                <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">Start Time</label>
                                <Input
                                  type="time"
                                  id="transfer_start_time"
                                  name="transfer_start_time"
                                  value={formData.transfer_start_time}
                                  // Use variant handler when variants enabled
                                  onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                                  className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] sm:text-xs text-gray-500 mb-1">End Time</label>
                                <Input
                                  type="time"
                                  id="transfer_end_time"
                                  name="transfer_end_time"
                                  value={formData.transfer_end_time}
                                  // Use variant handler when variants enabled
                                  onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                                  className="h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors"
                                />
                              </div>
                            </div>
                            
                            {(validationErrors.transfer_start_time || validationErrors.transfer_end_time) && (
                              <motion.p 
                                className="text-[11px] sm:text-xs text-red-500 flex items-center"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                {validationErrors.transfer_start_time || validationErrors.transfer_end_time}
                              </motion.p>
                            )}
                          </div>

                          {/* Days of Week */}
                          <div className="space-y-1.5 sm:space-y-2">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-green-500" />
                              Days of Week
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Select days when this transfer is active
                            </p>
                            
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => { // Updated onClick
                                    const currentDays = formData.transfer_days || []; // Ensure it's an array
                                    const newDays = currentDays.includes(index)
                                      ? currentDays.filter(d => d !== index)
                                      : [...currentDays, index].sort();
                                    
                                    if (formData.showLocationVariants) {
                                      // Use the direct variant handler for arrays/toggles
                                      handleVariantChangeDirectly('transfer_days', newDays);
                                    } else {
                                      // Original logic for non-variant mode
                                      setFormData(prev => ({ ...prev, transfer_days: newDays }));
                                    setHasUnsavedChanges(true);
                                    }
                                    
                                    // Clear validation error (common logic)
                                    if (validationErrors.transfer_days) {
                                      setValidationErrors(prev => {
                                        const newErrors = {...prev};
                                        delete newErrors.transfer_days;
                                        return newErrors;
                                      });
                                    }
                                  }}
                                  className={cn(
                                    "px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium transition-colors",
                                    // Use optional chaining for safety in includes check
                                    formData.transfer_days?.includes(index)
                                      ? "bg-green-100 text-green-800 border border-green-200"
                                      : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                                  )}
                                >
                                  {day.slice(0, 3)}
                                </button>
                              ))}
                            </div>
                            
                            {validationErrors.transfer_days && (
                              <motion.p 
                                className="text-[11px] sm:text-xs text-red-500 flex items-center"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                {validationErrors.transfer_days}
                              </motion.p>
                            )}
                          </div>
                              </div>
                            </div>
                    )}
                    
                    {/* API Content */}
                    {formData.type === 'api' && (
                      <ApiActionTab
                        formData={formData}
                        settingId={simpleActionId} // Pass the simple action ID for API testing (brain_simple_actions.id)
                        validationErrors={validationErrors}
                        showQuestions={showQuestions}
                        setShowQuestions={setShowQuestions}
                        selectedProviderId={selectedProviderId}
                        userIntegrationsMap={userIntegrationsMap}
                        availableProviders={availableProviders}
                        onFormChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                        onVariantChange={handleVariantChangeDirectly}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        showBearerToken={showBearerToken}
                        setShowBearerToken={setShowBearerToken}
                        showBasicPassword={showBasicPassword}
                        setShowBasicPassword={setShowBasicPassword}
                        showApiKey={showApiKey}
                        setShowApiKey={setShowApiKey}
                        onIntegrationSelection={handleIntegrationSelection}
                      />
                    )}
                    
                      {/* Note Fields Tab Content - Adapted from OrderPathwayBuilder's QuestionsTab */}
                      {formData.type === 'note' && (
                        <NoteFieldsTab
                          formData={formData}
                          setFormData={setFormData} // Or a more specific setter for note_fields_config
                          setFormDataByGrouping={setFormDataByGrouping} // If variants are used
                          activeLocationGrouping={activeLocationGrouping} // For variants
                          validationErrors={validationErrors.note_fields_individual || {}}
                          setValidationErrors={setValidationErrors} // To clear errors
                          setHasUnsavedChanges={setHasUnsavedChanges}
                          isMobile={isMobile}
                          // Pass specific handlers if needed, or a general update function
                          updateNoteFieldsConfig={(newFields) => {
                            if (formData.showLocationVariants && activeLocationGrouping) {
                                handleVariantChangeDirectly('note_fields_config', newFields, activeLocationGrouping);
                            } else {
                                setFormData(prev => ({ ...prev, note_fields_config: newFields }));
                            }
                            setHasUnsavedChanges(true);
                            // Clear general tab error if specific field errors are handled
                            if (validationErrors.note_fields_tab_error) {
                                setValidationErrors(prev => {
                                    const newErrors = {...prev};
                                    delete newErrors.note_fields_tab_error;
                                    return newErrors;
                                });
                            }
                          }}
                        />
                      )}
                      
                      {/* DTMF Content */}
                      {formData.type === 'dtmf' && (
                        <div className="space-y-5">
                          {/* DTMF Preview */}
                          <div className="space-y-1.5 sm:space-y-2">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">DTMF Preview</h3>
                            <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 sm:p-5">
                              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-4 relative">
                                <div className="flex items-center mb-3">
                                  <Hash className="h-4 w-4 text-orange-500 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">DTMF Input</span>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3 text-center">
                                  <div className="text-2xl font-mono text-gray-800 mb-2">
                                    {formData.dtmf_number_to_press || '123'}
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {formData.dtmf_number_to_press ? 'Will press these digits' : 'Enter digits to press'}
                                  </p>
                                </div>
                                
                                <div className="absolute -top-2 -left-2 bg-orange-100 text-orange-800 text-[9px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 rounded">
                                  Preview
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* DTMF Number Input */}
                          <div className="space-y-1.5 sm:space-y-2 pt-3 sm:pt-4 border-t border-gray-100">
                            <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                              <Hash className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-orange-500" />
                              Number to Press
                              <span className="text-red-500 ml-0.5">*</span>
                            </h3>
                            <p className="text-[10px] sm:text-xs text-gray-500">
                              Enter the digits to press on the phone keypad (0-9, *, #)
                            </p>
                            
                            <Input
                              id="dtmf_number_to_press"
                              name="dtmf_number_to_press"
                              value={formData.dtmf_number_to_press}
                              onChange={formData.showLocationVariants ? handleFormChangeWithVariants : handleChange}
                              placeholder="e.g., 1, 2, 123, *0#"
                              className={cn(
                                "h-9 sm:h-10 text-sm border-gray-200 bg-gray-50 focus:bg-white transition-colors font-mono",
                                validationErrors.dtmf_number_to_press && "border-red-300 focus:border-red-500 focus:ring-red-500"
                              )}
                            />
                            
                            {validationErrors.dtmf_number_to_press && (
                              <motion.p 
                                className="text-[11px] sm:text-xs text-red-500 flex items-center"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                {validationErrors.dtmf_number_to_press}
                              </motion.p>
                            )}
                          </div>
                          
                          {/* DTMF Tips */}
                          <div className="pt-3 sm:pt-4 border-t border-gray-100">
                            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 sm:p-4">
                              <h4 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center">
                                <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-1.5 text-orange-500" />
                                DTMF Usage Tips
                              </h4>
                              <ul className="mt-1.5 sm:mt-2 space-y-1.5 sm:space-y-2 text-[10px] sm:text-xs text-gray-600">
                                <li className="flex items-start">
                                  <div className="rounded-full h-1 w-1 bg-orange-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
                                  <span>Use single digits (1, 2, 3) for simple menu navigation</span>
                                </li>
                                <li className="flex items-start">
                                  <div className="rounded-full h-1 w-1 bg-orange-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
                                  <span>Use sequences (123, *0#) for complex IVR systems</span>
                                </li>
                                <li className="flex items-start">
                                  <div className="rounded-full h-1 w-1 bg-orange-400 mt-1 sm:mt-1.5 mr-1 sm:mr-1.5 flex-shrink-0" />
                                  <span>Common patterns: Press 1 for sales, Press 0 for operator</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Delete Tab */}
                  {activeTab === "delete" && !isNew && (
                    <div className="space-y-6 py-6">
                      <div className="flex flex-col items-center text-center max-w-lg mx-auto">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6"
                        >
                          <Trash className="h-10 w-10 text-red-500" />
                        </motion.div>
                        
                        <motion.h3
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1, duration: 0.3 }}
                          className="text-xl font-medium text-gray-900 mb-2"
                        >
                          Delete "{formData.name}"
                        </motion.h3>
                        
                        <motion.p
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2, duration: 0.3 }}
                          className="text-gray-500 mb-8"
                        >
                          This tool will permanently remove this tool and all associated data. 
                          This operation cannot be undone.
                        </motion.p>
                        
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3, duration: 0.3 }}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-6 w-full mb-8"
                        >
                          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start">
                              <Shield className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-1">
                                  This will delete:
                                </h4>
                                <ul className="space-y-2 mt-2">
                                  <li className="flex items-start text-sm text-gray-600">
                                    <div className="rounded-full h-1.5 w-1.5 bg-red-400 mt-1.5 mr-2 flex-shrink-0" />
                                    <span>The tool configuration and settings</span>
                                  </li>
                                  <li className="flex items-start text-sm text-gray-600">
                                    <div className="rounded-full h-1.5 w-1.5 bg-red-400 mt-1.5 mr-2 flex-shrink-0" />
                                    <span>All triggers and keywords</span>
                                  </li>
                                  <li className="flex items-start text-sm text-gray-600">
                                    <div className="rounded-full h-1.5 w-1.5 bg-red-400 mt-1.5 mr-2 flex-shrink-0" />
                                    <span>Message content and any associated links</span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                        
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4, duration: 0.3 }}
                          className="flex flex-col items-center"
                        >
                          <Button
                            type="button"
                            onClick={() => setIsDeleteModalOpen(true)}
                            className="bg-red-600 hover:bg-red-700 text-white mb-3 px-6 w-full sm:w-auto"
                          >
                            Delete This Tool
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setActiveTab("details")}
                            className="text-gray-500"
                          >
                            Cancel
                          </Button>
                        </motion.div>
                      </div>
                    </div>
                  )}
              </div>
            )} {/* End of tab content */}
          </div> {/* End of px-4 sm:px-6 div */}
         {/* End of !modalIsLoading && ( */} 
          
          {/* Footer with Create/Save Button - Only show in modal mode */}
          {!isInline && (
            <div className="border-t border-gray-200 bg-white px-4 sm:px-6 py-4">
              <div className="flex justify-end">
                <div className="flex items-center gap-2">
                  {onOpenActivationConditions && setting && (
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenActivationConditions(setting)}
                        className="text-gray-700 border-gray-200 hover:bg-gray-50"
                     >
                       <Clock className="w-4 h-4 mr-2" />
                       Conditions
                     </Button>
                  )}
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !hasUnsavedChanges}
                    size="sm"
                    className="bg-black hover:bg-gray-800"
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
  );

  if (isInline) {
    return renderContent();
  }

  return (
    <>
      {/* Modal Backdrop */}
      <motion.div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10050] flex items-center justify-center overflow-auto p-4"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleCloseAttempt}
      >
        {renderContent()}
      </motion.div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Tool"
        message={`Are you sure you want to delete "${formData.name || 'this tool'}"? This tool cannot be undone.`}
        confirmText="Delete Tool"
        confirmStyle="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        cancelText="Cancel"
      />
      
      {/* Unsaved Changes Modal */}
      <ConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={handleCloseConfirmed}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard Changes"
        confirmStyle="bg-red-600 hover:bg-red-700"
        cancelText="Continue Editing"
      />
      
      {/* New Category Modal */}
      <NewCategoryModal
        isOpen={isNewCategoryModalOpen}
        onClose={() => setIsNewCategoryModalOpen(false)}
        onSave={handleNewCategoryCreated}
      />
      
      {/* Simple Action Tool Modal - only render for non-inline mode */}
      {!isInline && (
        <SimpleActionToolModal
          isOpen={isToolTypeModalOpen}
          onClose={() => setIsToolTypeModalOpen(false)}
          onToolSelect={handleToolTypeSelect}
          isInline={isInline}
        />
      )}
      
      {/* Expanded Action Modal */}
      <AnimatePresence mode="wait">
        {expandedActionType && (
          <ExpandedActionModal
            expandedActionType={expandedActionType}
            setExpandedActionType={setExpandedActionType}
            formData={formData}
            setFormData={setFormData}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        )}
      </AnimatePresence>
      

    </>
  );
};

// --- NEW: NoteFieldsTab (adapted from OrderPathwayBuilder's QuestionsTab) ---
const NoteFieldsTab = ({
  formData,
  updateNoteFieldsConfig, // Specific updater for note_fields_config
  validationErrors, // Individual field errors: validationErrors.note_fields_individual
  setValidationErrors, // To clear errors
  setHasUnsavedChanges,
  isMobile
}) => {
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('string'); // Default to 'string' (Text)
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  // const [editingIndex, setEditingIndex] = useState(null); // Index in formData.note_fields_config - Replaced by editingFieldTempId
  const [editingFieldTempId, setEditingFieldTempId] = useState(null);

  // --- State for Follow-up Questions ---
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [parentFieldId, setParentFieldId] = useState(''); // Stores temp_id of the parent
  const [parentFieldTriggerRule, setParentFieldTriggerRule] = useState({ type: 'answered', value: undefined });
  const [selectedParentFieldDetails, setSelectedParentFieldDetails] = useState(null); // { temp_id, type, options, question }
  // --- End State for Follow-up Questions ---

  const noteFields = formData.note_fields_config || [];

  // Helper to generate a unique temporary ID for new fields before saving
  const generateTempId = () => `temp_note_field_${Math.random().toString(36).substr(2, 9)}`;

  // --- NEW: Effect to update selectedParentFieldDetails and reset trigger rule ---
  useEffect(() => {
    if (parentFieldId) {
      const parentField = noteFields.find(f => f.temp_id === parentFieldId);
      setSelectedParentFieldDetails(parentField ? { temp_id: parentField.temp_id, type: parentField.type, options: parentField.options || [], question: parentField.question } : null);
      setParentFieldTriggerRule({ type: 'answered', value: undefined }); // Reset rule
    } else {
      setSelectedParentFieldDetails(null);
      setParentFieldTriggerRule({ type: 'answered', value: undefined });
    }
  }, [parentFieldId, noteFields]);


  const handleAddOrUpdateField = () => {
    if (!questionText.trim()) {
      toast.error('Field label cannot be empty.');
      return;
    }
    if (questionType === 'enum' && options.length === 0) {
      toast.error('Please add at least one option for "Choose One" fields.');
      return;
    }

    let fieldCriteria = null;
    if (isFollowUp && parentFieldId && selectedParentFieldDetails) {
        let dbCondition = '';
        let dbValue = parentFieldTriggerRule.value;
        // For simple notes, criteria might be simpler. Example:
        // criteria: { parent_temp_id: 'temp_xyz', condition: 'equals', value: 'Yes' }
        // Or for "answered": { parent_temp_id: 'temp_xyz', condition: 'answered' }
        
        // This structure should align with what brain_simple_actions_fields.criteria expects
        // Based on OrderPathwayBuilder, it might be: { triggers: [{ field_id, condition, value, logic }] }
        // For simplicity here, we'll store a simpler structure directly if the DB column can take it,
        // or adapt as needed during the actual save to DB.
        // Let's aim for a compatible structure with OrderPathwayBuilder for `criteria.triggers`
        
        const triggers = [];
        switch (parentFieldTriggerRule.type) {
            case 'answered':
                dbCondition = 'answered';
                dbValue = undefined;
                break;
            case 'not_answered': // Assuming you might add this later
                dbCondition = 'not_answered';
                dbValue = undefined;
                break;
            case 'matches_specific_value':
                switch (selectedParentFieldDetails.type) {
                    case 'enum': // Choose One
                        dbCondition = 'equals_any_of'; // If multiple values can be selected for trigger, else 'equals'
                                                  // For "Choose One", parent usually has one value, so 'equals' is fine.
                                                  // If trigger can be "if parent is X OR Y", then 'equals_any_of'
                        dbCondition = 'equals'; // Simplified for "Choose One" parent.
                        dbValue = parentFieldTriggerRule.value; // Should be a single string option
                        break;
                    case 'boolean': // Yes/No
                        dbCondition = 'equals';
                        dbValue = parentFieldTriggerRule.value; // 'yes' or 'no' (or true/false if DB expects that for boolean criteria)
                        break;
                    default: // Text, Number - assuming direct equality for simplicity
                        dbCondition = 'equals';
                        dbValue = parentFieldTriggerRule.value;
                        break;
                }
                break;
            default: break;
        }

        if (dbCondition) {
            triggers.push({
                // Storing parent's temp_id here; will need resolution to actual DB ID on save
                parent_temp_id: parentFieldId, 
                condition: dbCondition,
                value: dbValue,
                // logic: 'AND' // Default if multiple triggers were possible
            });
        }
        if (triggers.length > 0) {
            fieldCriteria = { triggers }; // Match the structure if backend expects `triggers` array
        }
    }


    const newField = {
      temp_id: editingFieldTempId || generateTempId(),
      id: editingFieldTempId ? noteFields.find(f => f.temp_id === editingFieldTempId)?.id : undefined,
      question: questionText,
      type: questionType, // Directly use 'string', 'numeric', 'boolean', 'enum'
      options: questionType === 'enum' ? options : [],
      order: 0, // Will be set/updated below
      parent_field_temp_id: isFollowUp ? parentFieldId : null, // Store parent's temp_id
      criteria: fieldCriteria
    };

    let updatedFields;
    if (editingFieldTempId !== null) {
      const fieldIdx = noteFields.findIndex(f => f.temp_id === editingFieldTempId);
      if (fieldIdx === -1) {
        console.error("Editing field not found by temp_id", editingFieldTempId);
        toast.error("Error updating field.");
        resetForm();
        return;
      }
      newField.order = noteFields[fieldIdx].order; // Preserve order initially
      updatedFields = noteFields.map(f => (f.temp_id === editingFieldTempId ? newField : f));
      toast.success('Field updated');
    } else {
      // Assign order for new field (considering hierarchy)
      if (newField.parent_field_temp_id) {
          const siblings = noteFields.filter(f => f.parent_field_temp_id === newField.parent_field_temp_id);
          newField.order = siblings.length;
      } else {
          const topLevelFields = noteFields.filter(f => !f.parent_field_temp_id);
          newField.order = topLevelFields.length;
      }
      updatedFields = [...noteFields, newField];
      toast.success('Field added');
    }
    
    // Re-calculate order for all fields to ensure integrity, especially after add/edit/parent change
    const reOrderFields = (fields) => {
        const topLevel = fields.filter(f => !f.parent_field_temp_id).sort((a,b) => a.order - b.order);
        const childrenMap = fields.filter(f => f.parent_field_temp_id).reduce((acc, f) => {
            if (!acc[f.parent_field_temp_id]) acc[f.parent_field_temp_id] = [];
            acc[f.parent_field_temp_id].push(f);
            return acc;
        }, {});

        let globalOrder = 0;
        const result = [];

        function processLevel(items) {
            items.forEach(item => {
                result.push({ ...item, order: globalOrder++ });
                if (childrenMap[item.temp_id]) {
                    const sortedChildren = childrenMap[item.temp_id].sort((a,b) => a.order - b.order);
                    processLevel(sortedChildren);
                }
            });
        }
        processLevel(topLevel);
        return result;
    };

    updateNoteFieldsConfig(reOrderFields(updatedFields));
    resetForm();
  };

  const resetForm = () => {
    setQuestionText('');
    setQuestionType('string'); // Default to string
    setOptions([]);
    setNewOption('');
    setEditingFieldTempId(null);
    setIsFollowUp(false);
    setParentFieldId('');
    setSelectedParentFieldDetails(null);
    setParentFieldTriggerRule({ type: 'answered', value: undefined });

    if (editingFieldTempId && validationErrors[editingFieldTempId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[editingFieldTempId];
        if (Object.keys(newErrors).length === 0 && prev.note_fields_tab_error) {
          delete newErrors.note_fields_tab_error;
        }
        return newErrors;
      });
    }
  };
  
  const handleEditField = (fieldToEdit) => {
    setQuestionText(fieldToEdit.question);
    setQuestionType(fieldToEdit.type);
    setOptions(fieldToEdit.options || []);
    setNewOption('');
    setEditingFieldTempId(fieldToEdit.temp_id);

    if (fieldToEdit.parent_field_temp_id) {
      setIsFollowUp(true);
      setParentFieldId(fieldToEdit.parent_field_temp_id); // This will trigger useEffect to load parent details
      // Parse criteria (assuming simple structure for now)
      if (fieldToEdit.criteria && fieldToEdit.criteria.triggers && fieldToEdit.criteria.triggers.length > 0) {
        const critTrigger = fieldToEdit.criteria.triggers[0];
        // We need parent field type to correctly set up the rule value input
        // This is handled by the useEffect listening to parentFieldId
        let ruleType = 'answered';
        if (critTrigger.condition === 'answered' || critTrigger.condition === 'not_answered') {
            ruleType = critTrigger.condition;
        } else if (critTrigger.condition === 'equals' || critTrigger.condition === 'equals_any_of') {
            ruleType = 'matches_specific_value';
        }
        setParentFieldTriggerRule({ type: ruleType, value: critTrigger.value });
      } else {
        setParentFieldTriggerRule({ type: 'answered', value: undefined });
      }
    } else {
      setIsFollowUp(false);
      setParentFieldId('');
      setParentFieldTriggerRule({ type: 'answered', value: undefined });
    }
  };

  const handleDeleteField = (fieldToDeleteTempId) => {
    let fieldsAfterDelete = noteFields.filter(f => f.temp_id !== fieldToDeleteTempId);
    // Also remove any children of the deleted field
    fieldsAfterDelete = fieldsAfterDelete.filter(f => f.parent_field_temp_id !== fieldToDeleteTempId);
    
    // Re-calculate order
    const reOrderFields = (fields) => {
        const topLevel = fields.filter(f => !f.parent_field_temp_id).sort((a,b) => a.order - b.order);
        const childrenMap = fields.filter(f => f.parent_field_temp_id).reduce((acc, f) => {
            if (!acc[f.parent_field_temp_id]) acc[f.parent_field_temp_id] = [];
            acc[f.parent_field_temp_id].push(f);
            return acc;
        }, {});
        let globalOrder = 0;
        const result = [];
        function processLevel(items) {
            items.forEach(item => {
                result.push({ ...item, order: globalOrder++ });
                if (childrenMap[item.temp_id]) {
                    const sortedChildren = childrenMap[item.temp_id].sort((a,b) => a.order - b.order);
                    processLevel(sortedChildren);
                }
            });
        }
        processLevel(topLevel);
        return result;
    };

    updateNoteFieldsConfig(reOrderFields(fieldsAfterDelete));

    if (editingFieldTempId === fieldToDeleteTempId) {
      resetForm();
    }
    toast.success('Field deleted');
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setOptions(prev => [...prev, newOption.trim()]);
      setNewOption('');
      setHasUnsavedChanges(true);
    }
  };

  const handleRemoveOption = (optionIndex) => {
    setOptions(prev => prev.filter((_, idx) => idx !== optionIndex));
    setHasUnsavedChanges(true);
  };

  const moveField = (fieldTempId, direction) => {
    const fields = [...noteFields];
    const fieldIndex = fields.findIndex(f => f.temp_id === fieldTempId);
    if (fieldIndex === -1) return;

    const fieldToMove = fields[fieldIndex];
    const siblings = fields.filter(f => f.parent_field_temp_id === fieldToMove.parent_field_temp_id);
    const currentIndexInSiblings = siblings.findIndex(s => s.temp_id === fieldTempId);

    let targetIndexInSiblings;
    if (direction === 'up') {
        if (currentIndexInSiblings === 0) return; // Already at the top of its group
        targetIndexInSiblings = currentIndexInSiblings - 1;
    } else {
        if (currentIndexInSiblings === siblings.length - 1) return; // Already at the bottom
        targetIndexInSiblings = currentIndexInSiblings + 1;
    }
    
    // Swap order with the target sibling
    const otherSibling = siblings[targetIndexInSiblings];
    const fieldToMoveOriginalOrder = fieldToMove.order;
    fieldToMove.order = otherSibling.order;
    otherSibling.order = fieldToMoveOriginalOrder;

    // Sort all fields by parent, then by new order to reconstruct the flat list correctly
    const sortedFields = fields.sort((a, b) => {
        if (a.parent_field_temp_id !== b.parent_field_temp_id) {
            // This sort isn't perfect for multi-level, but good for siblings
            return (a.parent_field_temp_id || '').localeCompare(b.parent_field_temp_id || '');
        }
        return a.order - b.order;
    });
    
    // A more robust re-ordering might be needed if global order needs recalculation
    // For now, this simple swap should work for sibling reordering
    updateNoteFieldsConfig(sortedFields);
    setHasUnsavedChanges(true);
  };

  const getEligibleParentFields = () => {
    return noteFields.filter(f => {
      // Cannot be its own parent
      if (editingFieldTempId && f.temp_id === editingFieldTempId) return false;
      // Cannot be a child of the currently editing field (to prevent circular dependencies)
      if (editingFieldTempId && f.parent_field_temp_id === editingFieldTempId) return false;
      // A field that is already a follow-up cannot be a parent (simplification for now)
      // if (f.parent_field_temp_id) return false; // Allow follow-ups to be parents for multi-level
      return true;
    });
  };
  
  // --- Helper to get hierarchical fields for display ---
  const getHierarchicalNoteFieldsForDisplay = () => {
    const fields = [...(formData.note_fields_config || [])];
    const fieldMap = new Map(fields.map(f => [f.temp_id, { ...f, children: [] }]));
    const topLevelFields = [];

    for (const field of fields) {
        const mappedField = fieldMap.get(field.temp_id);
        if (field.parent_field_temp_id && fieldMap.has(field.parent_field_temp_id)) {
            const parent = fieldMap.get(field.parent_field_temp_id);
            if (parent) parent.children.push(mappedField);
        } else {
            topLevelFields.push(mappedField);
        }
    }
    
    // Sort children within each parent by their 'order'
    fieldMap.forEach(field => {
        if (field.children.length > 0) {
            field.children.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
    });
    // Sort top-level fields by their 'order'
    topLevelFields.sort((a, b) => (a.order || 0) - (b.order || 0));

    const displayList = [];
    function flatten(itemsToFlatten, depth) {
        for (const item of itemsToFlatten) {
            displayList.push({ ...item, depth });
            if (item.children.length > 0) {
                flatten(item.children, depth + 1);
            }
        }
    }
    flatten(topLevelFields, 0);
    return displayList;
  };
  const displayableFields = getHierarchicalNoteFieldsForDisplay();


  return (
    <div className="space-y-6 py-3 sm:py-4">
      {/* Add Field Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm sm:text-base font-medium text-gray-800">
            {editingFieldTempId ? 'Edit Note Field' : 'Add Note Field'}
          </h3>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
            Define the fields for taking notes.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
          <Input
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Field Label (e.g., Customer Name, Issue)"
            className="flex-1 h-9 sm:h-10 text-sm border-gray-200"
          />
          <Select value={questionType} onValueChange={(value) => {
              setQuestionType(value);
              if (value !== 'enum') setOptions([]); // Clear options if not enum type
          }}>
            <SelectTrigger className="w-full sm:w-48 h-9 sm:h-10 text-xs sm:text-sm border-gray-200">
              <SelectValue placeholder="Field Type" />
            </SelectTrigger>
                              <SelectContent className="z-[10025]">
              <SelectItem value="string">Text</SelectItem>
              <SelectItem value="numeric">Number</SelectItem>
              <SelectItem value="boolean">Yes/No</SelectItem>
              <SelectItem value="enum">Choose One</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            onClick={handleAddOrUpdateField}
            disabled={!questionText.trim()}
            className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3"
            size={isMobile ? "sm" : "default"}
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
            {editingFieldTempId !== null ? 'Update Field' : 'Add Field'}
          </Button>
          {editingFieldTempId !== null && (
              <Button
                  type="button"
                  variant="ghost"
                  onClick={resetForm}
                  className="text-xs sm:text-sm"
                  size={isMobile ? "sm" : "default"}
              >
                  Cancel Edit
              </Button>
          )}
        </div>
         {validationErrors && validationErrors[editingFieldTempId || 'new_field_general'] && (
          <motion.p
              className="text-[11px] sm:text-xs text-red-500 flex items-center mt-1"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
          >
              {validationErrors[editingFieldTempId || 'new_field_general']}
          </motion.p>
         )}

        {questionType === 'enum' && (
          <motion.div 
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50/50"
          >
            <Label className="text-xs font-medium text-gray-700 mb-2 block">
              Options for "Choose One"
            </Label>
            <div className="flex space-x-2 mb-3">
              <Input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                placeholder="Add an option"
                className="flex-1 h-8 text-sm border-gray-200 bg-white"
                onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } }}
              />
              <Button onClick={handleAddOption} variant="outline" size="sm" disabled={!newOption.trim()}>
                Add Option
              </Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-md p-2 max-h-[120px] overflow-y-auto">
              {options.length > 0 ? (
                options.map((opt, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center py-1.5 px-2 hover:bg-gray-50 rounded text-xs"
                  >
                    <span>{opt}</span>
                    <button onClick={() => handleRemoveOption(idx)} className="text-gray-400 hover:text-red-500">
                      <Trash size={14} />
                    </button>
                  </motion.div>
                ))
              ) : (
                <p className="text-gray-400 text-xs py-2 text-center">No options added yet.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* --- Follow-up Question Toggle & Settings --- */}
        <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700">Follow-up Question</h4>
                    <p className="text-[10px] sm:text-xs text-gray-500">
                        Show this field only if another field is answered a certain way.
                    </p>
                </div>
                <Switch
                    checked={isFollowUp}
                    onCheckedChange={(checked) => {
                        setIsFollowUp(checked);
                        if (!checked) {
                            setParentFieldId(''); // Reset parent when toggling off
                            setParentFieldTriggerRule({ type: 'answered', value: undefined });
                        }
                    }}
                    className="data-[state=checked]:bg-purple-500"
                    disabled={editingFieldTempId !== null && noteFields.some(f => f.parent_field_temp_id === editingFieldTempId)} // Disable if this field is a parent
                />
            </div>
        </div>

        {isFollowUp && (
            <motion.div
                initial={{ opacity: 0, y: -10, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                className="mt-2 space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50/50"
            >
                <div>
                    <Label htmlFor="parentField" className="text-xs font-medium text-gray-700 mb-1 block">
                        Show when field:
                    </Label>
                    <Select
                        value={parentFieldId}
                        onValueChange={setParentFieldId} // useEffect will handle dependent state changes
                    >
                        <SelectTrigger id="parentField" className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                            <SelectValue placeholder="Select parent field" />
                        </SelectTrigger>
                        <SelectContent className="z-[10025]"> {/* Higher z-index */}
                            {getEligibleParentFields().length === 0 && (
                                <SelectItem value="" disabled className="text-xs text-gray-400">No eligible parent fields</SelectItem>
                            )}
                            {getEligibleParentFields().map(f => (
                                <SelectItem key={f.temp_id} value={f.temp_id} className="text-sm">
                                    {f.question} ({f.type})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {parentFieldId && selectedParentFieldDetails && (
                    <>
                        <div className="mt-2 space-y-1">
                            <Label className="text-xs font-medium text-gray-700 mb-1 block">
                                This field should appear when "{selectedParentFieldDetails.question}"...
                            </Label>
                            <Select
                                value={parentFieldTriggerRule.type}
                                onValueChange={(type) => {
                                    let defaultValue;
                                    if (type === 'matches_specific_value') {
                                        if (selectedParentFieldDetails.type === 'enum') defaultValue = ''; // Default to first option or empty
                                        else if (selectedParentFieldDetails.type === 'boolean') defaultValue = 'true'; // Or 'yes'
                                        else defaultValue = '';
                                    } else {
                                        defaultValue = undefined;
                                    }
                                    setParentFieldTriggerRule({ type: type, value: defaultValue });
                                }}
                            >
                                <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="z-[10025]">
                                    <SelectItem value="answered">Is Answered</SelectItem>
                                    {/* <SelectItem value="not_answered">Is Not Answered</SelectItem> */}
                                    {(selectedParentFieldDetails.type === 'enum' || selectedParentFieldDetails.type === 'boolean' || selectedParentFieldDetails.type === 'string' || selectedParentFieldDetails.type === 'numeric') && (
                                        <SelectItem value="matches_specific_value">Matches Specific Value</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        {parentFieldTriggerRule.type === 'matches_specific_value' && (
                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                <Label className="text-xs font-medium text-gray-700 mb-0.5 block">
                                    Specify the value it should match:
                                </Label>
                                {selectedParentFieldDetails.type === 'enum' && (
                                    <Select
                                        value={parentFieldTriggerRule.value || ''}
                                        onValueChange={(val) => setParentFieldTriggerRule({ ...parentFieldTriggerRule, value: val })}
                                    >
                                        <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                                            <SelectValue placeholder="Select option to match" />
                                        </SelectTrigger>
                                        <SelectContent className="z-[10025]">
                                            {(selectedParentFieldDetails.options || []).map(opt => (
                                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {selectedParentFieldDetails.type === 'boolean' && (
                                    <Select
                                        value={String(parentFieldTriggerRule.value === undefined ? true : parentFieldTriggerRule.value)} // Default to 'true'
                                        onValueChange={(val) => setParentFieldTriggerRule({ ...parentFieldTriggerRule, value: val === 'true' })}
                                    >
                                        <SelectTrigger className="h-9 sm:h-10 text-sm border-gray-200 bg-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="z-[10025]">
                                            <SelectItem value="true">Yes</SelectItem>
                                            <SelectItem value="false">No</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                {(selectedParentFieldDetails.type === 'string' || selectedParentFieldDetails.type === 'numeric') && (
                                    <Input
                                        type={selectedParentFieldDetails.type === 'numeric' ? 'number' : 'text'}
                                        value={parentFieldTriggerRule.value || ''}
                                        onChange={(e) => setParentFieldTriggerRule({ ...parentFieldTriggerRule, value: e.target.value })}
                                        placeholder={`Enter expected ${selectedParentFieldDetails.type} value`}
                                        className="h-9 sm:h-10 text-sm border-gray-200 bg-white"
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </motion.div>
        )}
        {/* --- End Follow-up Question Settings --- */}
      </div>

      {/* Display Fields Section */}
      <div className="mt-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center mb-1 sm:mb-2">
              <HelpCircle size={isMobile ? 14 : 16} className="text-gray-600 mr-1.5 sm:mr-2" />
              <span className="text-xs text-gray-600 font-medium">Note Fields Preview</span>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              Fields will be presented in this order. Use arrows to reorder.
            </p>
          </div>
          {displayableFields.length > 0 && (
            <div className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
              {displayableFields.length} {displayableFields.length === 1 ? 'field' : 'fields'}
            </div>
          )}
        </div>

        <div className="min-h-[150px] bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 relative mt-2">
          {displayableFields.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-6 sm:py-8">
              <Notepad className="h-6 w-6 sm:h-8 sm:w-8 text-gray-300 mb-1.5 sm:mb-2" />
              <p className="text-xs sm:text-sm text-gray-400">No fields defined for this note.</p>
              <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1">Add fields above to structure the note.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayableFields.map((field, displayIdx) => (
                <motion.div
                  key={field.temp_id} 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                  style={{ marginLeft: `${field.depth * 20}px` }} // Indentation
                >
                  <div className="flex items-center border-b border-gray-100 px-3 py-2">
                    <div className="flex-shrink-0 mr-2 flex items-center">
                      {/* Display order number based on visual position in its own level */}
                       <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full w-5 h-5 flex items-center justify-center">
                        {field.order + 1} 
                      </span>
                    </div>
                    <div className="flex-1 flex items-center overflow-hidden">
                      <div className={`truncate text-xs px-2 py-0.5 rounded ${
                        field.type === 'boolean' ? 'bg-green-100 text-green-800' :
                        field.type === 'enum' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {field.type === 'string' ? 'Text' :
                         field.type === 'numeric' ? 'Number' :
                         field.type === 'boolean' ? 'Yes/No' :
                         field.type === 'enum' ? 'Choose One' : 'Unknown'}
                      </div>
                       {field.parent_field_temp_id && (
                        <span className="ml-2 text-[10px] text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-sm">
                            Follow-up
                        </span>
                       )}
                    </div>
                    <div className="flex items-center space-x-0.5">
                      {/* Simplified move for now, considers only siblings implicitly by affecting `order` */}
                      <button 
                        type="button" 
                        onClick={() => moveField(field.temp_id, 'up')} 
                        className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                        // Simple disable: if it's the first item OR first among its siblings
                        disabled={field.order === 0}
                       >
                        <CaretUp size={16} />
                      </button>
                      <button 
                        type="button" 
                        onClick={() => moveField(field.temp_id, 'down')} 
                        className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                        // Simple disable: if it's the last item OR last among its siblings
                        disabled={field.order === (noteFields.filter(f => f.parent_field_temp_id === field.parent_field_temp_id).length -1) }
                      >
                        <CaretDown size={16} />
                      </button>
                      <button type="button" onClick={() => handleEditField(field)} className="p-1 text-gray-400 hover:text-blue-500 focus:outline-none">
                        <Gear className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => handleDeleteField(field.temp_id)} className="p-1 text-gray-400 hover:text-red-500 focus:outline-none">
                        <Trash className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-sm text-gray-800">{field.question}</p>
                    {field.type === 'enum' && field.options && field.options.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {field.options.map((opt, optIdx) => (
                          <span key={optIdx} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {opt}
                          </span>
                        ))}
                      </div>
                    )}
                    {field.parent_field_temp_id && field.criteria && field.criteria.triggers && field.criteria.triggers.length > 0 && (
                        <div className="mt-1.5 text-[10px] text-gray-500 border-t border-gray-100 pt-1.5">
                           <span className="font-medium">Shows if:</span> {
                               noteFields.find(f => f.temp_id === field.parent_field_temp_id)?.question || 'Parent field'
                           } 
                           <span className="text-purple-600 ml-1">
                                ({field.criteria.triggers[0].condition}
                                {field.criteria.triggers[0].value !== undefined ? ` '${String(field.criteria.triggers[0].value)}'` : ''})
                           </span>
                        </div>
                    )}
                     {validationErrors && validationErrors[field.temp_id] && (
                        <motion.p
                            className="text-[10px] text-red-500 mt-1"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                        >
                           {validationErrors[field.temp_id]}
                        </motion.p>
                     )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
// --- END NoteFieldsTab ---

export default CallActionSettingsModal; 